
import { useEffect, useMemo, useState } from "react";
import Fuse from "fuse.js";
import JSZip from "jszip";

import muni from "./data/muni_saguache_mtc.json";
import county from "./data/county_saguache_mtc.json";
import state from "./data/state_crs.json";
import { getCourtDates2026 } from "./courts";

const JURIS = [
  { key: "MUNI_SAGUACHE", label: "Saguache Municipal" },
  { key: "COUNTY_SAGUACHE", label: "Saguache County" },
  { key: "STATE_CO", label: "Colorado State (CRS)" },
];

const ROLES = ["Deputy", "Supervisor", "Admin"];
const PASS_ON_TYPES = [
  "Officer Safety",
  "Shift Recap",
  "ATL / BOLO",
  "Extra Patrol Request",
  "Case Follow-Up",
  "Problem Property / Trespass",
  "Civil Issue",
  "Agency / Admin Notice",
  "Traffic / Roadway Issue",
  "Other",
];
const PASS_ON_PRIORITIES = ["Low", "Normal", "High"];
const ALLOWED = new Set(JURIS.map((x) => x.key));
const MODULE_SEEN_STORAGE_KEY = "patrol_ops_module_seen";
const AGENCY_EMAIL_DOMAIN = "@saguachecounty-co.gov";
const NOTEPAD_STORAGE_KEY = "patrol_ops_user_notepad_v1";

const FUEL_STORAGE_KEY = "patrol_ops_fuel_entries_v1";
const DOCUMENTS_STORAGE_KEY = "patrol_ops_documents_v1";
const DOCUMENT_CATEGORIES = [
  "Warrantless Arrest",
  "Domestic Violence",
  "Search Warrant",
  "Arrest Warrant",
  "Agency Miscellaneous",
];

const ARREST_WARRANT_AFFIDAVIT_TEMPLATE_BASE64 = "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";
const ARREST_WARRANT_TEMPLATE_BASE64 = "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";

function uid(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}
function getLocalJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}
function setLocalJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}
function loadFuelEntries() {
  const rows = getLocalJson(FUEL_STORAGE_KEY, []);
  return Array.isArray(rows) ? rows : [];
}
function saveFuelEntries(rows) {
  setLocalJson(FUEL_STORAGE_KEY, Array.isArray(rows) ? rows : []);
}
function loadDocuments() {
  const rows = getLocalJson(DOCUMENTS_STORAGE_KEY, []);
  return Array.isArray(rows) ? rows : [];
}
function saveDocuments(rows) {
  setLocalJson(DOCUMENTS_STORAGE_KEY, Array.isArray(rows) ? rows : []);
}
function toDateInputValue(date) {
  const dt = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(dt.getTime())) return "";
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function monthKeyFromDate(dateString) {
  if (!dateString) return "";
  const dt = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
}
function downloadTextFile(filename, contents) {
  const blob = new Blob([contents], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function formatBytes(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
}
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsDataURL(file);
  });
}
function dataUrlToBase64(dataUrl) {
  const raw = safeStr(dataUrl, "");
  const idx = raw.indexOf(",");
  return idx >= 0 ? raw.slice(idx + 1) : raw;
}
function base64ToBlob(base64, contentType = "application/octet-stream") {
  const byteCharacters = atob(base64);
  const sliceSize = 1024;
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i += 1) byteNumbers[i] = slice.charCodeAt(i);
    byteArrays.push(new Uint8Array(byteNumbers));
  }
  return new Blob(byteArrays, { type: contentType || "application/octet-stream" });
}
function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "download";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function base64ToUint8Array(base64) {
  const clean = safeStr(base64, "").replace(/^data:.*?;base64,/, "");
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
function escapeXmlText(value) {
  return safeStr(value, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function xmlReplacementText(value) {
  return escapeXmlText(value).replace(/\r?\n/g, "</w:t><w:br/><w:t>");
}
function stripWordDrawings(xml) {
  return safeStr(xml, "").replace(/<w:drawing>[\s\S]*?<\/w:drawing>/g, "");
}
function forcePlaceholderParagraphAlignment(xml, placeholder, alignment = "left") {
  const token = `{{${placeholder}}}`;
  const idx = xml.indexOf(token);
  if (idx < 0) return xml;
  const start = xml.lastIndexOf("<w:p", idx);
  const end = xml.indexOf("</w:p>", idx);
  if (start < 0 || end < 0) return xml;
  let paragraph = xml.slice(start, end + 6);
  if (paragraph.includes("<w:jc ")) {
    paragraph = paragraph.replace(/<w:jc\b[^>]*\/>/, `<w:jc w:val="${alignment}"/>`);
  } else if (paragraph.includes("<w:pPr>")) {
    paragraph = paragraph.replace("<w:pPr>", `<w:pPr><w:jc w:val="${alignment}"/>`);
  }
  return xml.slice(0, start) + paragraph + xml.slice(end + 6);
}
function sanitizeFilenamePart(value, fallback = "document") {
  const cleaned = safeStr(value, fallback).trim().replace(/[\\/:*?"<>|]+/g, "_").replace(/\s+/g, "_");
  return cleaned || fallback;
}
async function loadDocxZipFromPublic(paths, fallbackBase64) {
  const candidates = Array.isArray(paths) ? paths : [paths];

  for (const path of candidates) {
    try {
      const res = await fetch(path, { cache: "no-store" });
      if (!res.ok) continue;
      const buffer = await res.arrayBuffer();
      return await JSZip.loadAsync(buffer);
    } catch (err) {
      console.warn(`Failed to load template at ${path}`, err);
    }
  }

  // Fallback keeps the generator usable if the public file path is missing.
  return await JSZip.loadAsync(base64ToUint8Array(fallbackBase64));
}

async function generateDocxBlobFromZip(zip, replacements, options = {}) {
  const placeholders = Object.entries(replacements || {});
  const xmlFiles = Object.keys(zip.files).filter((name) => name.startsWith("word/") && name.endsWith(".xml"));
  let replacementCount = 0;

  for (const name of xmlFiles) {
    const file = zip.file(name);
    if (!file) continue;
    let xml = await file.async("string");
    if (options.stripDrawings) xml = stripWordDrawings(xml);
    if (options.leftAlignChargesText) xml = forcePlaceholderParagraphAlignment(xml, "chargesText", "left");
    for (const [key, value] of placeholders) {
      const token = `{{${key}}}`;
      const before = xml;
      xml = xml.split(token).join(xmlReplacementText(value));
      if (before !== xml) replacementCount += 1;
    }
    zip.file(name, xml);
  }

  if (replacementCount === 0) {
    throw new Error(
      "The arrest warrant template loaded, but no placeholders were found. Open the Word template and add fields like {{defendantName}}, {{dob}}, {{chargesText}}, {{probableCause}}, {{offenseDate}}, and {{date}}."
    );
  }

  return zip.generateAsync({ type: "blob" });
}

async function generateDocxBlobFromTemplateBase64(templateBase64, replacements, options = {}) {
  const zip = await JSZip.loadAsync(base64ToUint8Array(templateBase64));
  return generateDocxBlobFromZip(zip, replacements, options);
}
async function generateDocxFromTemplateBase64(templateBase64, replacements, filename) {
  const blob = await generateDocxBlobFromTemplateBase64(templateBase64, replacements);
  triggerBlobDownload(blob, filename);
}
async function generateArrestWarrantPacketZip(replacements, baseName) {
  const packetZip = new JSZip();

  const affidavitZip = await loadDocxZipFromPublic(
    [
      "/Documents/arrest-warrant/arrest-warrant-affidavit-template.docx",
      "/Documents/arrest-warrant/arrest-warrant-affidavit-template",
      "/documents/arrest-warrant/arrest-warrant-affidavit-template.docx",
      "/documents/arrest-warrant/arrest-warrant-affidavit-template",
    ],
    ARREST_WARRANT_AFFIDAVIT_TEMPLATE_BASE64
  );

  const warrantZip = await loadDocxZipFromPublic(
    [
      "/Documents/arrest-warrant/arrest-warrant-template.docx",
      "/Documents/arrest-warrant/arrest-warrant-template",
      "/documents/arrest-warrant/arrest-warrant-template.docx",
      "/documents/arrest-warrant/arrest-warrant-template",
    ],
    ARREST_WARRANT_TEMPLATE_BASE64
  );

  const affidavitBlob = await generateDocxBlobFromZip(affidavitZip, replacements, { leftAlignChargesText: true });
  const warrantBlob = await generateDocxBlobFromZip(warrantZip, replacements);

  packetZip.file(`Arrest_Warrant_Affidavit_${baseName}.docx`, affidavitBlob);
  packetZip.file(`Arrest_Warrant_${baseName}.docx`, warrantBlob);

  const packetBlob = await packetZip.generateAsync({ type: "blob" });
  triggerBlobDownload(packetBlob, `Arrest_Warrant_Packet_${baseName}.zip`);
}
async function loadRequiredDocxZipFromPublic(paths, label = "template") {
  const candidates = Array.isArray(paths) ? paths : [paths];

  for (const path of candidates) {
    try {
      const res = await fetch(path, { cache: "no-store" });
      if (!res.ok) continue;
      const buffer = await res.arrayBuffer();
      return await JSZip.loadAsync(buffer);
    } catch (err) {
      console.warn(`Failed to load ${label} at ${path}`, err);
    }
  }

  throw new Error(`Could not load ${label}. Confirm the DOCX exists in public/Documents/search-warrant with the exact required filename.`);
}

async function generateSearchWarrantPacketZip(replacements, baseName) {
  const packetZip = new JSZip();

  const affidavitZip = await loadRequiredDocxZipFromPublic(
    [
      "/Documents/search-warrant/search-warrant-affidavit-template.docx",
      "/documents/search-warrant/search-warrant-affidavit-template.docx",
    ],
    "search warrant affidavit template"
  );

  const warrantZip = await loadRequiredDocxZipFromPublic(
    [
      "/Documents/search-warrant/search-warrant-template.docx",
      "/documents/search-warrant/search-warrant-template.docx",
    ],
    "search warrant template"
  );

  const affidavitBlob = await generateDocxBlobFromZip(affidavitZip, replacements);
  const warrantBlob = await generateDocxBlobFromZip(warrantZip, replacements);

  packetZip.file(`Search_Warrant_Affidavit_${baseName}.docx`, affidavitBlob);
  packetZip.file(`Search_Warrant_${baseName}.docx`, warrantBlob);

  const packetBlob = await packetZip.generateAsync({ type: "blob" });
  triggerBlobDownload(packetBlob, `Search_Warrant_Packet_${baseName}.zip`);
}

function formatDateForCourt(value) {
  if (!value) return "";
  const raw = safeStr(value, "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = iso
    ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    : new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
}

function formatDateForCourtNoWrap(value) {
  return formatDateForCourt(value);
}

function formatCourtOathDate(value) {
  if (!value) return "";
  const raw = safeStr(value, "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = iso
    ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    : new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;

  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "long" });
  const year = d.getFullYear();
  const suffix = day % 100 >= 11 && day % 100 <= 13
    ? "th"
    : day % 10 === 1
      ? "st"
      : day % 10 === 2
        ? "nd"
        : day % 10 === 3
          ? "rd"
          : "th";

  return `${day}${suffix} day of ${month}, ${year}`;
}

function parseChargeLines(raw) {
  return safeStr(raw, "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 10)
    .map((line, index) => {
      const match = line.match(/^(?:C\.?R\.?S\.?\s*)?([0-9]{1,2}-[0-9A-Za-z.-]+(?:\([^)]+\))*)\s+(.+)$/i);
      return {
        number: String(index + 1),
        crs: match ? match[1].trim() : "",
        name: match ? match[2].trim() : line,
        raw: line,
      };
    });
}
function buildArrestWarrantReplacements(formData, currentUser) {
  function buildSearchWarrantReplacements(formData) {
  return {
    affiantName: formData.affiantName || "",
    locationDescription: formData.locationDescription || "",
    itemsToSeize: formData.itemsToSeize || "",
    probableCause: formData.probableCause || "",
    date: formatCourtOathDate(formData.filingDate),
  };
}
  const charges = parseChargeLines(formData.charges);
  const chargesText = charges.length
    ? (() => {
        const statuteParts = charges.map((c) => `C.R.S. ${c.crs || "________"}`.trim());
        const maxLen = Math.max(...statuteParts.map((part) => part.length), 12);
        return charges.map((c, index) => {
          const statutePart = statuteParts[index];
          const padding = " ".repeat(Math.max(2, maxLen - statutePart.length + 3));
          return `${statutePart}${padding}${c.name || ""}`.trimEnd();
        }).join("\n");
      })()
    : safeStr(formData.charges, "");
  const chargesInline = charges.length
    ? charges.map((c) => `C.R.S. ${c.crs || "________"} ${c.name || ""}`.trim()).join("; ")
    : safeStr(formData.charges, "");

  return {
    caseNumber: formData.caseNumber || "",
    judgeName: formData.judgeName || "________________________________",
    defendantName: formData.defendantName || "",
    dob: formData.dob || "",
    hair: formData.hair || "",
    height: formData.height || "",
    eyes: formData.eyes || "",
    weight: formData.weight || "",
    raceSex: formData.raceSex || "",
    dl: formData.dl || "",
    ssn: formData.ssn || "",
    alias: formData.alias || "None",
    aliasDob: formData.aliasDob || "NA",
    offenseDate: formatDateForCourt(formData.offenseDate),
    // The district warrant template has a fixed charge table area.
    // To support up to 10 charges without changing the court template layout,
    // each charge column is filled as a multi-line cell.
    charge1Number: charges.map((c) => c.number).join("\n"),
    charge1Crs: charges.map((c) => c.crs || "________").join("\n"),
    charge1Name: charges.map((c) => c.name || "").join("\n"),
    charge2Number: "",
    charge2Crs: "",
    charge2Name: "",
    charge3Number: "",
    charge3Crs: "",
    charge3Name: "",
    chargesText,
    chargesInline,
    date: formatCourtOathDate(formData.filingDate || formData.date),
    dateNoWrap: formatCourtOathDate(formData.filingDate || formData.date),
    filingDate: formatCourtOathDate(formData.filingDate || formData.date),
    year: String(new Date().getFullYear()),
    affiantName: formData.affiantName || currentUser?.displayName || currentUser?.username || "",
    affiantPhone: formData.affiantPhone || "",
    pronoun: formData.pronoun || "he/she",
    probableCause: formData.probableCause || "",
  };
}
function buildSearchWarrantReplacements(formData, currentUser) {
  const affiantName = formData.affiantName || currentUser?.displayName || currentUser?.username || "";
  const locationDescription = safeStr(formData.locationDescription, "").trim();
  const itemsToSeize = safeStr(formData.itemsToSeize, "").trim();
  const probableCause = safeStr(formData.probableCause, "").trim();
  const filingDate = formData.filingDate || formData.date || "";
  const courtDate = formatCourtOathDate(filingDate);

  return {
    caseNumber: formData.caseNumber || "",
    judgeName: formData.judgeName || "",
    affiantName,
    affiantTitle: formData.affiantTitle || "Deputy",
    locationDescription,
    locationDescriptionLocatedAt: locationDescription,
    itemsToSeize,
    probableCause,
    furtherOrders: formData.furtherOrders || "1. This search warrant and its supporting affidavit be SEALED until completion of the investigation, or until further ordered by the Court.",
    date: courtDate,
    dateNoWrap: courtDate,
    filingDate: courtDate,
    dateShort: formatDateForCourt(filingDate),
    year: filingDate ? String((new Date(`${filingDate}T00:00:00`)).getFullYear()) : String(new Date().getFullYear()),
  };
}

function safeNum(v) { const n = Number(v); return Number.isFinite(n) ? n : null; }
function fmtMoney0(v) { const n = safeNum(v); return n == null ? "—" : `$${n.toFixed(0)}`; }
function safeStr(v, fallback = "") {
  if (v == null) return fallback;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v)) return v.map((x) => safeStr(x, "")).filter(Boolean).join(", ");
  try { return JSON.stringify(v); } catch { return String(v); }
}
function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(`${dateString}T00:00:00`);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
}
function formatDateTime(dateString) {
  if (!dateString) return "";
  const dt = new Date(dateString);
  return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleString();
}
function canDeleteRecords(currentUser) { return ["Supervisor", "Admin"].includes(currentUser?.role); }
function canEditRecords(currentUser) { return ["Supervisor", "Admin"].includes(currentUser?.role); }
function isAdmin(currentUser) { return currentUser?.role === "Admin"; }
function sortByDateDesc(a, b) { return new Date(b || 0) - new Date(a || 0); }
function normalizeUsername(value) { return safeStr(value, "").trim().toLowerCase(); }
function normalizeListResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.topUsers)) return data.topUsers;
  return [];
}
function normalizeUserResponse(data) { return data?.user || data?.session || data || null; }
function getPassOnSeenKey(type) { return `passon_${type}`; }

async function apiFetch(path, options = {}) {
  const method = (options.method || "GET").toUpperCase();
  const fetchOptions = {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  };
  if (method === "GET" && !fetchOptions.cache) fetchOptions.cache = "no-store";

  const res = await fetch(path, fetchOptions);
  let data = null;
  try { data = await res.json(); } catch {}
  if (!res.ok) throw new Error(data?.error || data?.message || `Request failed (${res.status})`);
  return data;
}

function getModuleSeenMap() {
  try {
    const raw = localStorage.getItem(MODULE_SEEN_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}
function saveModuleSeenMap(map) { localStorage.setItem(MODULE_SEEN_STORAGE_KEY, JSON.stringify(map)); }
function getUserModuleSeen(userId) { const map = getModuleSeenMap(); return map[userId] || {}; }
function markModuleSeen(userId, moduleName) {
  if (!userId || !moduleName) return;
  const map = getModuleSeenMap();
  const userMap = map[userId] || {};
  userMap[moduleName] = new Date().toISOString();
  map[userId] = userMap;
  saveModuleSeenMap(map);
}
function getModuleItemTimestamp(item) { return item?.updatedAt || item?.createdAt || null; }
function getUnreadModuleCount(userId, moduleName, items) {
  if (!userId) return 0;
  const userSeen = getUserModuleSeen(userId);
  const lastSeen = userSeen[moduleName];
  if (!lastSeen) return Array.isArray(items) ? items.length : 0;
  const lastSeenTime = new Date(lastSeen).getTime();
  return (Array.isArray(items) ? items : []).filter((item) => {
    const ts = getModuleItemTimestamp(item);
    if (!ts) return false;
    const itemTime = new Date(ts).getTime();
    return Number.isFinite(itemTime) && itemTime > lastSeenTime;
  }).length;
}

const COURT_FMT = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
function parseIsoDateOnly(dateStr) {
  if (typeof dateStr !== "string") return null;
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(dt.getTime()) ? null : dt;
}
function formatTimePretty(t) {
  if (typeof t !== "string") return "";
  const s = t.trim().toLowerCase();
  if (!s) return "";
  let ss = s;
  const m1 = ss.match(/^(\d{1,2})(\d{2})(a|p)$/);
  if (m1) ss = `${m1[1]}:${m1[2]}${m1[3]}`;
  const m = ss.match(/^(\d{1,2}):(\d{2})(a|p)?$/);
  if (!m) return t;
  let hh = Number(m[1]);
  const mm = m[2];
  const ap = m[3];
  if (ap) {
    if (hh === 0) hh = 12;
    if (hh > 12) hh = hh % 12;
    return `${hh}:${mm} ${ap.toUpperCase()}M`.replace("AMM", "AM").replace("PMM", "PM");
  }
  return `${hh}:${mm}`;
}
function normalizeCourtDates(raw) {
  const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const items = arr.map((x) => {
    if (x && typeof x === "object" && !Array.isArray(x) && !(x instanceof Date)) {
      const dt = parseIsoDateOnly(x.date);
      const dateLabel = dt ? COURT_FMT.format(dt) : safeStr(x.date, "");
      const timeLabel = formatTimePretty(x.time);
      const court = safeStr(x.court, "");
      const label = [dateLabel, timeLabel ? `• ${timeLabel}` : "", court ? `— ${court}` : ""].filter(Boolean).join(" ");
      return label ? { sort: dt ? dt.getTime() : Number.POSITIVE_INFINITY, label } : null;
    }
    if (typeof x === "string") {
      const dt = parseIsoDateOnly(x) || new Date(x);
      return { sort: Number.isNaN(dt.getTime()) ? Number.POSITIVE_INFINITY : dt.getTime(), label: Number.isNaN(dt.getTime()) ? x : COURT_FMT.format(dt) };
    }
    return null;
  }).filter(Boolean);
  const seen = new Set();
  return items.filter((it) => !seen.has(it.label) && seen.add(it.label)).sort((a,b)=>a.sort-b.sort).map((x)=>x.label);
}
function strictFilter(dataset, q) {
  const needle = q.trim().toLowerCase();
  if (!needle) return [];
  return dataset.filter((item) => {
    const citation = safeStr(item?.citation, "").toLowerCase();
    const code = safeStr(item?.commonCode, "").toLowerCase();
    const desc = safeStr(item?.description, "").toLowerCase();
    return citation.includes(needle) || code.includes(needle) || desc.includes(needle);
  });
}
function looksLikeSpeeding(row) {
  const citation = safeStr(row?.citation, "").toUpperCase();
  const desc = safeStr(row?.description, "").toLowerCase();
  return row?.isSpeeding === true || citation.includes("1101") || citation.includes("42-4-1101") || desc.includes("speeding") || desc.includes("over posted limit");
}
function inferRangeFromRow(row) {
  const desc = safeStr(row?.description, "");
  const citation = safeStr(row?.citation, "");
  if (safeNum(row?.mphMin) != null || row?.mphMax === null || safeNum(row?.mphMax) != null) return { min: safeNum(row?.mphMin), max: row?.mphMax == null ? null : safeNum(row?.mphMax) };
  let m = desc.match(/(\d+)\s*-\s*(\d+)/); if (m) return { min: Number(m[1]), max: Number(m[2]) };
  m = desc.match(/(\d+)\s*\+/); if (m) return { min: Number(m[1]), max: null };
  if (citation.includes("1101(1)")) return { min: 1, max: 4 };
  if (citation.includes("1101(2)")) return { min: 5, max: 9 };
  if (citation.includes("1101(3)")) return { min: 10, max: 19 };
  if (citation.includes("1101(4)")) return { min: 20, max: 24 };
  if (citation.includes("1101(5)")) return { min: 25, max: 39 };
  if (citation.includes("1101(6)")) return { min: 40, max: null };
  return null;
}
function pickSpeedingBracket(dataset, mphOver) {
  if (mphOver == null || Number.isNaN(mphOver) || mphOver < 1) return null;
  for (const row of dataset.filter(looksLikeSpeeding)) {
    const range = inferRangeFromRow(row);
    if (!range) continue;
    if ((range.min == null || mphOver >= range.min) && (range.max == null || mphOver <= range.max)) return { ...row, isSpeeding: true, mphMin: range.min, mphMax: range.max };
  }
  return null;
}
function generateCodeName(existingChecks = []) {
  const words = ["ALPHA","BRAVO","CHARLIE","DELTA","ECHO","FALCON","RANGER","SIERRA","TANGO","VECTOR","PATROL","GUARD"];
  let codeName = "", isUnique = false;
  while (!isUnique) {
    codeName = `${words[Math.floor(Math.random() * words.length)]}-${Math.floor(10 + Math.random() * 90)}`;
    isUnique = !existingChecks.some((check) => check.codeName === codeName);
  }
  return codeName;
}
function purgeExpiredVacationChecks(checks) {
  const now = new Date();
  return checks.filter((check) => {
    const expiresAt = check.expiresAt ? new Date(check.expiresAt) : check.returnDate ? new Date(`${check.returnDate}T23:59:59`) : null;
    if (!expiresAt || Number.isNaN(expiresAt.getTime())) return true;
    if (!check.expiresAt && check.returnDate) expiresAt.setDate(expiresAt.getDate() + 1);
    return expiresAt > now;
  });
}
function purgeExpiredPassOns(entries) {
  const now = new Date();
  return entries.filter((entry) => !entry.expirationDate || new Date(`${entry.expirationDate}T23:59:59`) > now);
}

function AuthScreen({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [registerData, setRegisterData] = useState({ displayName: "", username: "", email: "", password: "", confirmPassword: "" });

  async function handleLoginSubmit(e) {
    e.preventDefault();
    try {
      setLoading(true);
      const data = await apiFetch("/api/login", {
        method: "POST",
        body: JSON.stringify({
          username: normalizeUsername(loginData.username),
          password: loginData.password,
        }),
      });
      const user = normalizeUserResponse(data);
      if (!user) throw new Error("No session returned.");
      onLogin(user);
    } catch (err) {
      alert(err.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  }

  async function handleRegisterSubmit(e) {
    e.preventDefault();
    const displayName = registerData.displayName.trim();
    const username = normalizeUsername(registerData.username);
    const email = registerData.email.trim().toLowerCase();
    const password = registerData.password;
    const confirmPassword = registerData.confirmPassword;
    if (!displayName || !username || !email || !password || !confirmPassword) return alert("Please complete all fields.");
    if (!email.endsWith(AGENCY_EMAIL_DOMAIN)) return alert(`Email must end in ${AGENCY_EMAIL_DOMAIN}`);
    if (password !== confirmPassword) return alert("Passwords do not match.");

    try {
      setLoading(true);
      const data = await apiFetch("/api/register", {
        method: "POST",
        body: JSON.stringify({ displayName, username, email, password }),
      });

      if (data?.pendingApproval) {
        alert("Account created and pending admin approval.");
        setMode("login");
        return;
      }

      const user = normalizeUserResponse(data);
      if (!user) throw new Error("No session returned.");
      onLogin(user);
    } catch (err) {
      alert(err.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.authWrapper}>
      <div style={styles.authCard}>
        <h1 style={styles.authTitle}>Patrol Operations App</h1>
        <p style={styles.authSubtitle}>Agency registration is restricted to {AGENCY_EMAIL_DOMAIN} emails.</p>
        <div style={styles.authToggleRow}>
          <button style={{ ...styles.authToggleButton, ...(mode === "login" ? styles.authToggleActive : {}) }} type="button" onClick={() => setMode("login")}>Login</button>
          <button style={{ ...styles.authToggleButton, ...(mode === "register" ? styles.authToggleActive : {}) }} type="button" onClick={() => setMode("register")}>Register</button>
        </div>
        {mode === "login" ? (
          <form onSubmit={handleLoginSubmit} style={styles.form}>
            <div><label style={styles.label}>Username</label><input style={styles.input} value={loginData.username} onChange={(e)=>setLoginData({...loginData, username:e.target.value})} /></div>
            <div><label style={styles.label}>Password</label><input style={styles.input} type="password" value={loginData.password} onChange={(e)=>setLoginData({...loginData, password:e.target.value})} /></div>
            <button style={styles.primaryButton} disabled={loading}>{loading ? "Logging In..." : "Login"}</button>
          </form>
        ) : (
          <form onSubmit={handleRegisterSubmit} style={styles.form}>
            <div><label style={styles.label}>Display Name</label><input style={styles.input} value={registerData.displayName} onChange={(e)=>setRegisterData({...registerData, displayName:e.target.value})} /></div>
            <div><label style={styles.label}>Username</label><input style={styles.input} value={registerData.username} onChange={(e)=>setRegisterData({...registerData, username:e.target.value})} /></div>
            <div><label style={styles.label}>Agency Email</label><input style={styles.input} value={registerData.email} onChange={(e)=>setRegisterData({...registerData, email:e.target.value})} /></div>
            <div><label style={styles.label}>Password</label><input style={styles.input} type="password" value={registerData.password} onChange={(e)=>setRegisterData({...registerData, password:e.target.value})} /></div>
            <div><label style={styles.label}>Confirm Password</label><input style={styles.input} type="password" value={registerData.confirmPassword} onChange={(e)=>setRegisterData({...registerData, confirmPassword:e.target.value})} /></div>
            <button style={styles.successButton} disabled={loading}>{loading ? "Registering..." : "Register"}</button>
          </form>
        )}
      </div>
    </div>
  );
}

function TrafficModule() {
  const [jurisdiction, setJurisdiction] = useState("COUNTY_SAGUACHE");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [courtChoice, setCourtChoice] = useState("");
  const [mphOver, setMphOver] = useState("");
  const [stateMax75, setStateMax75] = useState(false);
  const [quickSpeedingMode, setQuickSpeedingMode] = useState(false);

  useEffect(() => { if (!ALLOWED.has(jurisdiction)) setJurisdiction("COUNTY_SAGUACHE"); }, [jurisdiction]);
  const rawList = useMemo(() => jurisdiction === "MUNI_SAGUACHE" ? muni : jurisdiction === "COUNTY_SAGUACHE" ? county : state, [jurisdiction]);
  const dataset = useMemo(() => jurisdiction === "STATE_CO" ? rawList.filter((x) => String(x?.citation || "").trim().startsWith("42-")) : rawList.filter((x) => String(x?.citation || "").trim().toUpperCase().startsWith("MTC")), [rawList, jurisdiction]);
  useEffect(() => { setSelected(null); setCourtChoice(""); setQuery(""); setMphOver(""); setStateMax75(false); setQuickSpeedingMode(false); }, [jurisdiction]);
  const fuse = useMemo(() => new Fuse(dataset, { includeScore: true, shouldSort: true, ignoreLocation: true, threshold: 0.32, minMatchCharLength: 2, keys: [{ name: "citation", weight: 4 }, { name: "commonCode", weight: 3 }, { name: "description", weight: 2 }] }), [dataset]);
  const results = useMemo(() => {
    if (quickSpeedingMode) return dataset.filter(looksLikeSpeeding).slice(0, 20);
    const q = query.trim();
    if (!q) return dataset.slice(0, 30);
    const strictHits = strictFilter(dataset, q);
    return strictHits.length > 0 ? strictHits.slice(0, 50) : fuse.search(q).slice(0, 50).map((r)=>r.item);
  }, [query, dataset, fuse, quickSpeedingMode]);
  const mphNum = useMemo(() => mphOver === "" ? null : Number(mphOver), [mphOver]);
  const shownViolation = useMemo(() => quickSpeedingMode ? pickSpeedingBracket(dataset, mphNum) : (looksLikeSpeeding(selected) ? pickSpeedingBracket(dataset, mphNum) || selected : selected), [quickSpeedingMode, dataset, mphNum, selected]);
  const courtDates = useMemo(() => normalizeCourtDates(getCourtDates2026({ jurisdiction, requiresSummons: !!shownViolation?.requiresSummons, stateMax75 })), [jurisdiction, shownViolation?.requiresSummons, stateMax75]);

  return (
    <div style={styles.trafficWrapper}>
      <h2 style={styles.h2}>Traffic Module</h2>
      <div style={styles.jurisRow}>
        {JURIS.map((t) => (
          <button key={t.key} onClick={() => setJurisdiction(t.key)} style={{ ...styles.filterButton, background: jurisdiction === t.key ? "#111" : "#fff", color: jurisdiction === t.key ? "#fff" : "#111" }}>{t.label}</button>
        ))}
      </div>
      <div style={styles.jurisRow}>
        <button onClick={() => { setQuickSpeedingMode((v) => !v); setSelected(null); setQuery(""); }} style={{ ...styles.filterButton, background: quickSpeedingMode ? "#111" : "#fff", color: quickSpeedingMode ? "#fff" : "#111" }}>Quick Speeding</button>
        {jurisdiction === "STATE_CO" && <label style={styles.inlineRow}><input type="checkbox" checked={stateMax75} onChange={(e)=>setStateMax75(e.target.checked)} /><span>75+ MPH zone</span></label>}
      </div>
{!quickSpeedingMode ? (
  <input
    style={styles.searchInput}
    value={query}
    onChange={(e) => setQuery(e.target.value)}
    placeholder="Search..."
  />
) : (
  <div style={styles.jurisRow}>
    <input
      style={styles.searchInput}
      type="number"
      min="1"
      step="1"
      value={mphOver}
      onChange={(e) => setMphOver(e.target.value)}
      placeholder="MPH over limit"
    />
  </div>
)}      <div style={styles.trafficGrid}>
        <div style={styles.resultsPanel}>
          <div style={styles.panelTitle}>Results</div>
          {results.map((v, i) => (
            <div key={`${v.citation}-${i}`} style={styles.resultRow} onClick={()=>setSelected(v)}>
              <div style={styles.resultCitation}>{safeStr(v?.citation, "—")}</div>
              <div style={styles.resultDescription}>{safeStr(v?.description, "")}</div>
            </div>
          ))}
        </div>
        <div style={styles.detailsPanel}>
          <div style={styles.panelTitle}>Violation Details</div>
          {shownViolation ? <>
            <div style={styles.detailLine}><strong>Citation:</strong> {safeStr(shownViolation.citation, "—")}</div>
            <div style={styles.detailLine}><strong>Description:</strong> {safeStr(shownViolation.description, "—")}</div>
            <div style={styles.moneyGrid}>
              <div><strong>Fine:</strong> {shownViolation.requiresSummons ? "SUMMONS" : fmtMoney0(shownViolation.fine)}</div>
              <div><strong>Surcharge:</strong> {shownViolation.requiresSummons ? "SUMMONS" : fmtMoney0(shownViolation.surcharge)}</div>
            </div>
            <select style={styles.searchInput} value={courtChoice} onChange={(e)=>setCourtChoice(e.target.value)}>
              <option value="">Select Court Date</option>
              {courtDates.map((label) => <option key={label} value={label}>{label}</option>)}
            </select>
          </> : <div style={styles.emptyState}>Select a violation.</div>}
        </div>
      </div>
    </div>
  );
}

function VacationHouseCheck({ currentUser, refreshSignal, onDataChange }) {
  const [showForm, setShowForm] = useState(false);
  const [vacationChecks, setVacationChecks] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ residentName: "", phone: "", address: "", city: "", returnDate: "" });
  const canDelete = canDeleteRecords(currentUser);
  const canEdit = canEditRecords(currentUser);

  async function loadVacationChecks() {
    try {
      setLoading(true);
      const data = await apiFetch("/api/security-checks");
      setVacationChecks(purgeExpiredVacationChecks(normalizeListResponse(data)));
    } catch (err) { alert(err.message || "Failed to load security checks."); } finally { setLoading(false); }
  }
  useEffect(() => { if (currentUser?.id) markModuleSeen(currentUser.id, "security"); }, [currentUser]);
  useEffect(() => { loadVacationChecks(); }, [refreshSignal]);
  const sortedVacationChecks = useMemo(() => [...vacationChecks].sort((a,b)=>new Date(a.returnDate)-new Date(b.returnDate)), [vacationChecks]);
  function resetForm() { setFormData({ residentName: "", phone: "", address: "", city: "", returnDate: "" }); setEditingId(null); setShowForm(false); }
  function handleFormChange(e) { const {name, value} = e.target; setFormData((p)=>({...p,[name]:value})); }
  function handleEdit(check) { setEditingId(check.id); setFormData({ residentName: check.residentName || "", phone: check.phone || "", address: check.address || "", city: check.city || "", returnDate: check.returnDate || "" }); setShowForm(true); }
  async function handleVacationSubmit(e) {
    e.preventDefault();
    const { residentName, phone, address, city, returnDate } = formData;
    if (!residentName || !phone || !address || !city || !returnDate) return alert("Please complete all fields.");
    const submittedBy = currentUser?.displayName || currentUser?.username || "Unknown User";
    try {
      if (editingId) {
        await apiFetch(`/api/security-checks?id=${encodeURIComponent(editingId)}`, { method: "PUT", body: JSON.stringify({ ...formData, updatedBy: submittedBy }) });
        alert("Security check updated.");
      } else {
        const codeName = generateCodeName(vacationChecks);
        await apiFetch("/api/security-checks", { method: "POST", body: JSON.stringify({ ...formData, codeName, submittedBy }) });
        alert(`Security check created.\nCode Name: ${codeName}`);
      }
      markModuleSeen(currentUser?.id, "security"); resetForm(); await loadVacationChecks(); onDataChange?.();
    } catch (err) { alert(err.message || "Failed to save security check."); }
  }
  async function handleDelete(id) {
    try { await apiFetch(`/api/security-checks?id=${encodeURIComponent(id)}`, { method:"DELETE" }); await loadVacationChecks(); onDataChange?.(); }
    catch (err) { alert(err.message || "Failed to delete security check."); }
  }
  return (
    <div>
      <div style={styles.topActions}><button style={styles.primaryButton} onClick={()=>{setEditingId(null);setShowForm(true);}}>New Submission</button></div>
      {showForm && <div style={styles.sectionCard}>
        <h2 style={styles.h2}>{editingId ? "Edit Security Check" : "New Security Check"}</h2>
        <form onSubmit={handleVacationSubmit} style={styles.form}>
          <div style={styles.formGrid}>
            {["residentName","phone","address","city","returnDate"].map((name) => (
              <div key={name}>
                <label style={styles.label}>{name === "residentName" ? "Name of Resident" : name === "returnDate" ? "Date of Resident Return" : name.charAt(0).toUpperCase()+name.slice(1)}</label>
                <input style={styles.input} type={name === "returnDate" ? "date" : "text"} name={name} value={formData[name]} onChange={handleFormChange} />
              </div>
            ))}
          </div>
          <div style={styles.topActions}><button style={styles.successButton}>{editingId ? "Save Changes" : "Save Submission"}</button><button type="button" style={styles.secondaryButton} onClick={resetForm}>Cancel</button></div>
        </form>
      </div>}
      <div style={styles.sectionCard}>
        <h2 style={styles.h2}>Active Security Check</h2>
        <div style={styles.vacationList}>
          {loading ? <div style={styles.vacationCard}>Loading...</div> : sortedVacationChecks.map((check) => (
            <div key={check.id} style={styles.vacationCard}>
              <div style={styles.vacationCardHeader}>
                <span style={styles.codeBadge}>{check.codeName}</span>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {canEdit && <button style={styles.secondaryButton} onClick={()=>handleEdit(check)}>Edit</button>}
                  {canDelete && <button style={styles.dangerButton} onClick={()=>handleDelete(check.id)}>Delete</button>}
                </div>
              </div>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Resident:</span> {check.residentName}</div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Phone:</span> {check.phone}</div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>City:</span> {check.city}</div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Return Date:</span> {formatDate(check.returnDate)}</div>
                <div style={{ ...styles.detailItem, gridColumn: "1 / -1" }}><span style={styles.detailLabel}>Address:</span> {check.address}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RedTaggedModule({ currentUser, refreshSignal, onDataChange }) {
  const [showForm, setShowForm] = useState(false);
  const [entries, setEntries] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ dateTagged: "", make: "", model: "", color: "", location: "", caseNumber: "" });
  const canDelete = canDeleteRecords(currentUser);
  const canEdit = canEditRecords(currentUser);

  async function loadEntries() { try { setLoading(true); const data = await apiFetch("/api/red-tagged"); setEntries(normalizeListResponse(data)); } catch (err) { alert(err.message || "Failed to load red tagged vehicle submissions."); } finally { setLoading(false); } }
  useEffect(() => { if (currentUser?.id) markModuleSeen(currentUser.id, "redtagged"); }, [currentUser]);
  useEffect(() => { loadEntries(); }, [refreshSignal]);
  const sortedEntries = useMemo(() => [...entries].sort((a, b) => sortByDateDesc(a.dateTagged, b.dateTagged)), [entries]);
  function resetForm() { setFormData({ dateTagged: "", make: "", model: "", color: "", location: "", caseNumber: "" }); setEditingId(null); setShowForm(false); }
  function handleFormChange(e) { const { name, value } = e.target; setFormData((prev) => ({ ...prev, [name]: value })); }
  function handleEdit(entry) { setEditingId(entry.id); setFormData({ dateTagged: entry.dateTagged || "", make: entry.make || "", model: entry.model || "", color: entry.color || "", location: entry.location || "", caseNumber: entry.caseNumber || "" }); setShowForm(true); }
  async function handleSubmit(e) {
    e.preventDefault();
    try {
      const submittedBy = currentUser?.displayName || currentUser?.username || "Unknown User";
      if (editingId) await apiFetch(`/api/red-tagged?id=${encodeURIComponent(editingId)}`, { method: "PUT", body: JSON.stringify({ ...formData, updatedBy: submittedBy }) });
      else await apiFetch("/api/red-tagged", { method: "POST", body: JSON.stringify({ ...formData, submittedBy }) });
      resetForm(); await loadEntries(); onDataChange?.();
    } catch (err) { alert(err.message || "Failed to save red tagged vehicle submission."); }
  }
  async function handleDelete(id) { try { await apiFetch(`/api/red-tagged?id=${encodeURIComponent(id)}`, { method: "DELETE" }); await loadEntries(); onDataChange?.(); } catch (err) { alert(err.message || "Failed to delete red tagged vehicle submission."); } }
  return (
    <div>
      <div style={styles.topActions}><button style={styles.primaryButton} onClick={()=>{setEditingId(null);setShowForm(true);}}>New Submission</button></div>
      {showForm && <div style={styles.sectionCard}>
        <h2 style={styles.h2}>{editingId ? "Edit Red Tagged Vehicle" : "New Red Tagged Vehicle"}</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGrid}>
            {["dateTagged","make","model","color","location","caseNumber"].map((name)=>(
              <div key={name}><label style={styles.label}>{name === "dateTagged" ? "Date of Tag" : name === "caseNumber" ? "Case Number" : name.charAt(0).toUpperCase()+name.slice(1)}</label>
              <input style={styles.input} type={name === "dateTagged" ? "date" : "text"} name={name} value={formData[name]} onChange={handleFormChange} /></div>
            ))}
          </div>
          <div style={styles.topActions}><button style={styles.successButton}>{editingId ? "Save Changes" : "Save Submission"}</button><button type="button" style={styles.secondaryButton} onClick={resetForm}>Cancel</button></div>
        </form>
      </div>}
      <div style={styles.sectionCard}>
        <h2 style={styles.h2}>Red Tagged Vehicles</h2>
        <div style={styles.vacationList}>
          {loading ? <div style={styles.vacationCard}>Loading...</div> : sortedEntries.map((entry) => (
            <div key={entry.id} style={styles.vacationCard}>
              <div style={styles.vacationCardHeader}>
                <span style={styles.codeBadge}>{safeStr(entry.make)} {safeStr(entry.model)}</span>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {canEdit && <button style={styles.secondaryButton} onClick={()=>handleEdit(entry)}>Edit</button>}
                  {canDelete && <button style={styles.dangerButton} onClick={()=>handleDelete(entry.id)}>Delete</button>}
                </div>
              </div>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Date of Tag:</span> {formatDate(entry.dateTagged)}</div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Color:</span> {entry.color}</div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Case Number:</span> {entry.caseNumber}</div>
                <div style={{ ...styles.detailItem, gridColumn: "1 / -1" }}><span style={styles.detailLabel}>Location:</span> {entry.location}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PassOnModule({ currentUser, refreshSignal, onDataChange, activeType = "", showAll = false }) {
  const [showForm, setShowForm] = useState(false);
  const [entries, setEntries] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    submissionType: activeType || PASS_ON_TYPES[0],
    title: "",
    details: "",
    priority: "Normal",
    relatedPerson: "",
    relatedVehicle: "",
    location: "",
    caseNumber: "",
    expirationDate: "",
  });
  const canManageAllPassOns = canEditRecords(currentUser);

  function isOwnPassOnEntry(entry) {
    const submittedBy = safeStr(entry?.submittedBy, "").trim().toLowerCase();
    const currentNames = [
      currentUser?.displayName,
      currentUser?.username,
      currentUser?.email,
    ]
      .map((value) => safeStr(value, "").trim().toLowerCase())
      .filter(Boolean);
    return submittedBy && currentNames.includes(submittedBy);
  }

  function canEditPassOnEntry(entry) {
    return canManageAllPassOns || isOwnPassOnEntry(entry);
  }

  function canDeletePassOnEntry(entry) {
    return canManageAllPassOns || isOwnPassOnEntry(entry);
  }

  async function loadEntries() {
    try {
      setLoading(true);
      const data = await apiFetch("/api/pass-ons");
      setEntries(purgeExpiredPassOns(normalizeListResponse(data)));
    } catch (err) {
      alert(err.message || "Failed to load pass ons.");
    } finally { setLoading(false); }
  }

  useEffect(() => { if (currentUser?.id && activeType && !showAll) markModuleSeen(currentUser.id, getPassOnSeenKey(activeType)); }, [currentUser, activeType, showAll]);
  useEffect(() => { loadEntries(); const id = setInterval(loadEntries, 15000); return ()=>clearInterval(id); }, [refreshSignal]);
  const filteredEntries = useMemo(() => showAll ? entries : entries.filter((e) => e.submissionType === activeType), [entries, activeType, showAll]);
  const sortedEntries = useMemo(() => [...filteredEntries].sort((a,b)=>sortByDateDesc(a.createdAt || a.updatedAt, b.createdAt || b.updatedAt)), [filteredEntries]);

  function resetForm() {
    setFormData({ submissionType: activeType || PASS_ON_TYPES[0], title: "", details: "", priority: "Normal", relatedPerson: "", relatedVehicle: "", location: "", caseNumber: "", expirationDate: "" });
    setEditingId(null); setExpandedId(null); setShowForm(false);
  }
  function handleFormChange(e) { const { name, value } = e.target; setFormData((prev) => ({ ...prev, [name]: value })); }
  function handleEdit(entry) { setEditingId(entry.id); setFormData({ submissionType: entry.submissionType || activeType || PASS_ON_TYPES[0], title: entry.title || "", details: entry.details || "", priority: entry.priority || "Normal", relatedPerson: entry.relatedPerson || "", relatedVehicle: entry.relatedVehicle || "", location: entry.location || "", caseNumber: entry.caseNumber || "", expirationDate: entry.expirationDate || "" }); setShowForm(true); }
  async function handleSubmit(e) {
    e.preventDefault();
    const submissionType = showAll ? formData.submissionType : activeType;
    const payload = { ...formData, submissionType, submittedBy: currentUser?.displayName || currentUser?.username || "Unknown User", updatedBy: currentUser?.displayName || currentUser?.username || "Unknown User" };
    try {
      if (editingId) await apiFetch(`/api/pass-ons?id=${encodeURIComponent(editingId)}`, { method: "PUT", body: JSON.stringify(payload) });
      else await apiFetch("/api/pass-ons", { method: "POST", body: JSON.stringify(payload) });
      if (currentUser?.id && submissionType) markModuleSeen(currentUser.id, getPassOnSeenKey(submissionType));
      resetForm(); await loadEntries(); onDataChange?.();
    } catch (err) { alert(err.message || "Failed to save pass on."); }
  }
  async function handleDelete(id) { try { await apiFetch(`/api/pass-ons?id=${encodeURIComponent(id)}`, { method:"DELETE" }); await loadEntries(); onDataChange?.(); } catch (err) { alert(err.message || "Failed to delete pass on."); } }
  function priorityStyle(priority) { return priority === "High" ? styles.highPriorityPill : priority === "Low" ? styles.lowPriorityPill : styles.normalPriorityPill; }

  return (
    <div>
      <div style={styles.topActions}><button style={styles.primaryButton} onClick={()=>{setEditingId(null);setShowForm(true);}}>New Submission</button></div>
      {showForm && <div style={styles.sectionCard}>
        <h2 style={styles.h2}>{editingId ? "Edit Pass On" : "New Pass On"}</h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Submission Type</label>
              {showAll ? (
                <select style={styles.input} name="submissionType" value={formData.submissionType} onChange={handleFormChange}>{PASS_ON_TYPES.map((type)=><option key={type} value={type}>{type}</option>)}</select>
              ) : (
                <input style={{ ...styles.input, background: "#f3f4f6" }} value={activeType} readOnly />
              )}
            </div>
            <div><label style={styles.label}>Priority</label><select style={styles.input} name="priority" value={formData.priority} onChange={handleFormChange}>{PASS_ON_PRIORITIES.map((priority)=><option key={priority} value={priority}>{priority}</option>)}</select></div>
            <div style={{ gridColumn: "1 / -1" }}><label style={styles.label}>Title / Short Label</label><input style={styles.input} name="title" value={formData.title} onChange={handleFormChange} /></div>
            <div style={{ gridColumn: "1 / -1" }}><label style={styles.label}>Details / Narrative</label><textarea style={styles.textarea} name="details" value={formData.details} onChange={handleFormChange} /></div>
            <div><label style={styles.label}>Related Person</label><input style={styles.input} name="relatedPerson" value={formData.relatedPerson} onChange={handleFormChange} /></div>
            <div><label style={styles.label}>Related Vehicle</label><input style={styles.input} name="relatedVehicle" value={formData.relatedVehicle} onChange={handleFormChange} /></div>
            <div><label style={styles.label}>Location</label><input style={styles.input} name="location" value={formData.location} onChange={handleFormChange} /></div>
            <div><label style={styles.label}>Case Number</label><input style={styles.input} name="caseNumber" value={formData.caseNumber} onChange={handleFormChange} /></div>
            <div><label style={styles.label}>Expiration Date</label><input style={styles.input} type="date" name="expirationDate" value={formData.expirationDate} onChange={handleFormChange} /></div>
          </div>
          <div style={styles.topActions}><button style={styles.successButton}>{editingId ? "Save Changes" : "Save Submission"}</button><button type="button" style={styles.secondaryButton} onClick={resetForm}>Cancel</button></div>
        </form>
      </div>}
      <div style={styles.sectionCard}>
        <h2 style={styles.h2}>{showAll ? "All Pass Ons" : `${activeType} Pass Ons`}</h2>
        <div style={styles.vacationList}>
          {loading ? <div style={styles.vacationCard}>Loading...</div> : sortedEntries.map((entry) => (
            <div key={entry.id} style={styles.vacationCard}>
              <button type="button" style={styles.passOnCollapseButton} onClick={()=>setExpandedId((prev)=>prev===entry.id?null:entry.id)}>
                <div style={styles.vacationCardHeader}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={styles.codeBadge}>{entry.submissionType}</span>
                    <span style={priorityStyle(entry.priority)}>{entry.priority}</span>
                  </div>
                  <span style={styles.passOnToggleText}>{formatDateTime(entry.createdAt || entry.updatedAt)}</span>
                </div>
                <div style={styles.passOnTitleRow}>{entry.title}</div>
              </button>
              {expandedId === entry.id && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ marginBottom: 10, whiteSpace: "pre-wrap" }}>{entry.details}</div>
                  <div style={styles.detailGrid}>
                    <div style={styles.detailItem}><span style={styles.detailLabel}>Related Person:</span> {entry.relatedPerson || "—"}</div>
                    <div style={styles.detailItem}><span style={styles.detailLabel}>Related Vehicle:</span> {entry.relatedVehicle || "—"}</div>
                    <div style={styles.detailItem}><span style={styles.detailLabel}>Location:</span> {entry.location || "—"}</div>
                    <div style={styles.detailItem}><span style={styles.detailLabel}>Case Number:</span> {entry.caseNumber || "—"}</div>
                  </div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:12 }}>
                    {canEditPassOnEntry(entry) && <button style={styles.secondaryButton} onClick={()=>handleEdit(entry)}>Edit</button>}
                    {canDeletePassOnEntry(entry) && <button style={styles.dangerButton} onClick={()=>handleDelete(entry.id)}>Delete</button>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PassOnHome({ currentUser, passOnItems, onOpenType, onOpenAll, onBack }) {
  return (
    <section>
      <div style={styles.topActions}><button style={styles.secondaryButton} onClick={onBack}>← Back to Home</button></div>
      <div style={styles.sectionCard}>
        <h2 style={styles.h2}>Pass On Modules</h2>
        <div style={styles.moduleGrid}>
          {isAdmin(currentUser) && (
            <div style={styles.moduleCard}>
              <div style={styles.moduleCardHeader}>
                <h3 style={styles.h3}>All Pass Ons</h3>
                {passOnItems.length > 0 ? <span style={styles.alertBadge}>{passOnItems.length}</span> : null}
              </div>
              <button style={styles.primaryButton} onClick={onOpenAll}>Open</button>
            </div>
          )}
          {PASS_ON_TYPES.map((type) => {
            const itemsForType = passOnItems.filter((x) => x.submissionType === type);
            const count = getUnreadModuleCount(currentUser?.id, getPassOnSeenKey(type), itemsForType);
            return (
              <div key={type} style={styles.moduleCard}>
                <div style={styles.moduleCardHeader}>
                  <h3 style={styles.h3}>{type}</h3>
                  {count > 0 && <span style={styles.alertBadge}>{count}</span>}
                </div>
                <button style={styles.primaryButton} onClick={() => onOpenType(type)}>Open</button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AdminDashboardModule({ refreshSignal, onOpenUserManagement }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ summary: {}, topUsers: [], usageByModule: [], recentEvents: [], pendingUsers: [] });

  async function loadDashboard() {
    try {
      setLoading(true);
      const result = await apiFetch("/api/dashboard");
      setData(result || { summary: {}, topUsers: [], usageByModule: [], recentEvents: [], pendingUsers: [] });
    } catch (err) {
      alert(err.message || "Failed to load admin dashboard.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDashboard(); }, [refreshSignal]);

  const summary = data.summary || {};
  const pendingUsers = data.pendingUsers || [];
  const recentEvents = data.recentEvents || [];

  return (
    <div>
      <div style={styles.sectionCard}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <h2 style={styles.h2}>Admin Dashboard</h2>
          <button style={styles.primaryButton} onClick={onOpenUserManagement}>Open User Management</button>
        </div>
        {loading ? <div>Loading...</div> : (
          <>
            <div style={styles.summaryGrid}>
              <div style={styles.summaryCard}><div style={styles.summaryLabel}>Total Users</div><div style={styles.summaryValue}>{summary.totalUsers || 0}</div></div>
              <div style={styles.summaryCard}><div style={styles.summaryLabel}>Approved</div><div style={styles.summaryValue}>{summary.approvedUsers || 0}</div></div>
              <div style={styles.summaryCard}><div style={styles.summaryLabel}>Pending</div><div style={styles.summaryValue}>{summary.pendingUsers || 0}</div></div>
              <div style={styles.summaryCard}><div style={styles.summaryLabel}>Disabled</div><div style={styles.summaryValue}>{summary.disabledUsers || 0}</div></div>
              <div style={styles.summaryCard}><div style={styles.summaryLabel}>Deputies</div><div style={styles.summaryValue}>{summary.deputyUsers || 0}</div></div>
              <div style={styles.summaryCard}><div style={styles.summaryLabel}>Supervisors</div><div style={styles.summaryValue}>{summary.supervisorUsers || 0}</div></div>
              <div style={styles.summaryCard}><div style={styles.summaryLabel}>Admins</div><div style={styles.summaryValue}>{summary.adminUsers || 0}</div></div>
              <div style={styles.summaryCard}><div style={styles.summaryLabel}>Logins Today</div><div style={styles.summaryValue}>{summary.loginsToday || 0}</div></div>
            </div>

            <h3 style={styles.h3}>Pending User Approvals</h3>
            <div style={styles.userTableWrapper}>
              <table style={styles.userTable}>
                <thead>
                  <tr>
                    <th style={styles.userTableTh}>Name</th>
                    <th style={styles.userTableTh}>Username</th>
                    <th style={styles.userTableTh}>Email</th>
                    <th style={styles.userTableTh}>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.length === 0 ? (
                    <tr><td style={styles.userTableTd} colSpan={4}>No pending users.</td></tr>
                  ) : pendingUsers.map((user) => (
                    <tr key={user.id}>
                      <td style={styles.userTableTd}>{user.displayName}</td>
                      <td style={styles.userTableTd}>{user.username}</td>
                      <td style={styles.userTableTd}>{user.email}</td>
                      <td style={styles.userTableTd}>{formatDateTime(user.createdAt) || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={styles.h3}>User Login Frequency</h3>
            <div style={styles.userTableWrapper}>
              <table style={styles.userTable}>
                <thead><tr><th style={styles.userTableTh}>User</th><th style={styles.userTableTh}>Role</th><th style={styles.userTableTh}>Login Count</th><th style={styles.userTableTh}>Last Login</th></tr></thead>
                <tbody>
                  {(data.topUsers || []).length === 0 ? (
                    <tr><td style={styles.userTableTd} colSpan={4}>No login activity yet.</td></tr>
                  ) : (data.topUsers || []).map((user) => (
                    <tr key={user.username}>
                      <td style={styles.userTableTd}>{user.displayName} ({user.username})</td>
                      <td style={styles.userTableTd}>{user.role}</td>
                      <td style={styles.userTableTd}>{user.loginCount || 0}</td>
                      <td style={styles.userTableTd}>{formatDateTime(user.lastLoginAt) || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={styles.h3}>Recent Audit Activity</h3>
            <div style={styles.userTableWrapper}>
              <table style={styles.userTable}>
                <thead><tr><th style={styles.userTableTh}>Time</th><th style={styles.userTableTh}>User</th><th style={styles.userTableTh}>Event</th><th style={styles.userTableTh}>Module</th><th style={styles.userTableTh}>Details</th></tr></thead>
                <tbody>
                  {recentEvents.length === 0 ? (
                    <tr><td style={styles.userTableTd} colSpan={5}>No audit activity yet.</td></tr>
                  ) : recentEvents.map((event) => (
                    <tr key={event.id}>
                      <td style={styles.userTableTd}>{formatDateTime(event.createdAt) || "—"}</td>
                      <td style={styles.userTableTd}>{event.username || "—"}</td>
                      <td style={styles.userTableTd}>{event.eventType || "—"}</td>
                      <td style={styles.userTableTd}>{event.moduleName || "—"}</td>
                      <td style={styles.userTableTd}>{event.details || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 style={styles.h3}>Usage by Module</h3>
            <div style={styles.userTableWrapper}>
              <table style={styles.userTable}>
                <thead><tr><th style={styles.userTableTh}>Module</th><th style={styles.userTableTh}>Event Count</th></tr></thead>
                <tbody>
                  {(data.usageByModule || []).length === 0 ? (
                    <tr><td style={styles.userTableTd} colSpan={2}>No module activity yet.</td></tr>
                  ) : (data.usageByModule || []).map((row) => (
                    <tr key={row.moduleName}><td style={styles.userTableTd}>{row.moduleName}</td><td style={styles.userTableTd}>{row.count}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function UserManagementModule({ currentUser, refreshSignal }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  async function refreshUsers() {
    try {
      setLoading(true);
      const data = await apiFetch("/api/users");
      setUsers(normalizeListResponse(data));
    } catch (err) {
      alert(err.message || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refreshUsers(); }, [refreshSignal]);

  async function handleRoleChange(userId, newRole) {
    try {
      await apiFetch(`/api/users?id=${encodeURIComponent(userId)}`, {
        method: "PUT",
        body: JSON.stringify({ role: newRole }),
      });
      await refreshUsers();
    } catch (err) {
      alert(err.message || "Failed to update user role.");
    }
  }

  async function handleApprove(userId) {
    try {
      await apiFetch(`/api/users?id=${encodeURIComponent(userId)}`, {
        method: "PUT",
        body: JSON.stringify({ isApproved: true }),
      });
      await refreshUsers();
    } catch (err) {
      alert(err.message || "Failed to approve user.");
    }
  }

  async function handleDisableToggle(user) {
    try {
      await apiFetch(`/api/users?id=${encodeURIComponent(user.id)}`, {
        method: "PUT",
        body: JSON.stringify({ isDisabled: !user.isDisabled }),
      });
      await refreshUsers();
    } catch (err) {
      alert(err.message || "Failed to update account status.");
    }
  }

  async function handleDeleteUser(user) {
    if (user.id === currentUser.id) {
      return alert("You cannot delete your own account.");
    }
    if (!window.confirm(`Delete user "${user.username}"? This cannot be undone.`)) {
      return;
    }

    try {
      await apiFetch(`/api/users?id=${encodeURIComponent(user.id)}`, {
        method: "DELETE",
      });
      await refreshUsers();
    } catch (err) {
      alert(err.message || "Failed to delete user.");
    }
  }

  return (
    <div style={styles.sectionCard}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}><h2 style={styles.h2}>User Management</h2><div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><span style={styles.codeBadge}>Pending: {users.filter((x) => !x.isApproved).length}</span><span style={styles.codeBadge}>Supervisors: {users.filter((x) => x.role === "Supervisor").length}</span></div></div>
      {loading ? <div>Loading...</div> : (
        <div style={styles.userTableWrapper}>
          <table style={styles.userTable}>
            <thead>
              <tr>
                <th style={styles.userTableTh}>Name</th>
                <th style={styles.userTableTh}>Username</th>
                <th style={styles.userTableTh}>Email</th>
                <th style={styles.userTableTh}>Role</th>
                <th style={styles.userTableTh}>Approval</th>
                <th style={styles.userTableTh}>Login Count</th>
                <th style={styles.userTableTh}>Last Login</th>
                <th style={styles.userTableTh}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isSelf = user.id === currentUser.id;
                return (
                  <tr key={user.id}>
                    <td style={styles.userTableTd}>{user.displayName}</td>
                    <td style={styles.userTableTd}>{user.username}</td>
                    <td style={styles.userTableTd}>{user.email}</td>
                    <td style={styles.userTableTd}>
                      <select
                        style={styles.compactInput}
                        value={user.role || "Deputy"}
                        disabled={isSelf}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      >
                        {ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
                      </select>
                    </td>
                    <td style={styles.userTableTd}>
                      {user.isApproved ? <span style={styles.activePill}>Approved</span> : <span style={styles.disabledPill}>Pending</span>}
                    </td>
                    <td style={styles.userTableTd}>{user.loginCount || 0}</td>
                    <td style={styles.userTableTd}>{formatDateTime(user.lastLoginAt) || "—"}</td>
                    <td style={styles.userTableTd}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {!user.isApproved && (
                          <button style={styles.successButton} onClick={() => handleApprove(user.id)}>
                            Approve
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            style={user.isDisabled ? styles.successButton : styles.dangerButton}
                            onClick={() => handleDisableToggle(user)}
                          >
                            {user.isDisabled ? "Enable" : "Disable"}
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            style={styles.dangerButton}
                            onClick={() => handleDeleteUser(user)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



function FuelModule({ currentUser }) {
  const [entries, setEntries] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    gallons: "",
    location: "",
  });

  const canEdit = canEditRecords(currentUser);
  const canDelete = canDeleteRecords(currentUser);

  function refreshEntries() {
    setEntries(loadFuelEntries());
  }

  useEffect(() => {
    refreshEntries();
  }, []);

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => sortByDateDesc(a.createdAt, b.createdAt)),
    [entries]
  );

  function resetForm() {
    setEditingId(null);
    setFormData({
      gallons: "",
      location: "",
    });
  }

  function handleEdit(entry) {
    if (!canEdit) return;
    setEditingId(entry.id);
    setFormData({
      gallons: entry.gallons == null ? "" : String(entry.gallons),
      location: entry.location || "",
    });
    window.scrollTo(0, 0);
  }

  function handleDelete(id) {
    if (!canDelete) return;
    if (!window.confirm("Delete this fuel entry?")) return;
    const next = loadFuelEntries().filter((entry) => entry.id !== id);
    saveFuelEntries(next);
    refreshEntries();
    if (editingId === id) resetForm();
  }

  function handleSubmit(e) {
    e.preventDefault();

    const gallons = Number(formData.gallons);
    if (!Number.isFinite(gallons) || gallons <= 0) {
      return alert("Gallons must be greater than zero.");
    }

    if (!["north", "south"].includes(formData.location)) {
      return alert("Select location.");
    }

    const now = new Date().toISOString();

    const payload = {
      id: editingId || uid("fuel"),
      gallons: Number(gallons.toFixed(2)),
      location: formData.location,
      submittedBy: currentUser?.displayName || currentUser?.username || "Unknown User",
      createdAt: now,
      updatedAt: now,
    };

    const existing = loadFuelEntries();
    const next = editingId
      ? existing.map((entry) =>
          entry.id === editingId
            ? { ...entry, ...payload, createdAt: entry.createdAt || now }
            : entry
        )
      : [payload, ...existing];

    saveFuelEntries(next);
    refreshEntries();
    resetForm();
    alert(editingId ? "Fuel entry updated." : "Fuel entry saved.");
  }

  return (
    <div style={styles.sectionCard}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <h2 style={styles.h2}>Fuel Module</h2>
          <p style={styles.muted}>Quick fuel entry with automatic date and time stamp. Reports are available in Admin Reports.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formGrid}>
          <div>
            <label style={styles.label}>Gallons</label>
            <input
              style={styles.input}
              type="number"
              min="0"
              step="0.01"
              name="gallons"
              value={formData.gallons}
              onChange={(e) => setFormData({ ...formData, gallons: e.target.value })}
            />
          </div>

          <div>
            <label style={styles.label}>Location</label>
            <select
              style={styles.input}
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            >
              <option value="">Select</option>
              <option value="north">North</option>
              <option value="south">South</option>
            </select>
          </div>
        </div>

        <div style={styles.topActions}>
          <button style={styles.primaryButton} type="submit">
            {editingId ? "Update Entry" : "Save Entry"}
          </button>
          {editingId && (
            <button style={styles.secondaryButton} type="button" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>

      {canEdit && sortedEntries.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h3 style={styles.h3}>Recent Fuel Entries</h3>
          <div style={styles.vacationList}>
            {sortedEntries.slice(0, 5).map((entry) => (
              <div key={entry.id} style={styles.vacationCard}>
                <div style={styles.vacationCardHeader}>
                  <span style={styles.codeBadge}>{safeStr(entry.gallons, "—")} gal • {safeStr(entry.location, "—")}</span>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {canEdit && <button style={styles.secondaryButton} type="button" onClick={() => handleEdit(entry)}>Edit</button>}
                    {canDelete && <button style={styles.dangerButton} type="button" onClick={() => handleDelete(entry.id)}>Delete</button>}
                  </div>
                </div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Submitted:</span> {formatDateTime(entry.createdAt)}</div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>By:</span> {safeStr(entry.submittedBy, "—")}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function ArrestWarrantGeneratorModule({ currentUser }) {
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    caseNumber: "",
    judgeName: "",
    defendantName: "",
    dob: "",
    hair: "",
    height: "",
    eyes: "",
    weight: "",
    raceSex: "",
    dl: "",
    ssn: "",
    alias: "None",
    aliasDob: "NA",
    offenseDate: "",
    charges: "",
    filingDate: toDateInputValue(new Date()),
    affiantName: currentUser?.displayName || currentUser?.username || "",
    affiantPhone: "",
    pronoun: "he/she",
    probableCause: "",
  });

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function requireFields() {
    const required = [
      ["caseNumber", "Case number"],
      ["defendantName", "Defendant name"],
      ["dob", "DOB"],
      ["offenseDate", "Offense date"],
      ["charges", "Charges"],
      ["probableCause", "Probable cause narrative"],
      ["affiantName", "Affiant name"],
    ];

    for (const [key, label] of required) {
      if (!safeStr(formData[key], "").trim()) {
        alert(`${label} is required.`);
        return false;
      }
    }
    return true;
  }

  async function handleGenerateDocx() {
    if (!requireFields()) return;

    try {
      setGenerating(true);
      const replacements = buildArrestWarrantReplacements(formData, currentUser);
      const baseName = `${sanitizeFilenamePart(formData.caseNumber, "case")}_${sanitizeFilenamePart(formData.defendantName, "defendant")}`;

      await generateArrestWarrantPacketZip(replacements, baseName);
    } catch (err) {
      alert(err.message || "Failed to generate warrant packet.");
    } finally {
      setGenerating(false);
    }
  }



  return (
    <div style={styles.sectionCard}>
      <h2 style={styles.h2}>Arrest Warrant Generator</h2>
      <p style={styles.muted}>
        Uses the Saguache arrest warrant and affidavit templates you provided. The narrative is inserted into the affidavit only. The DOCX packet downloads as one ZIP containing both Word files.
      </p>

      <form style={styles.form} onSubmit={(e) => e.preventDefault()}>
        <div style={styles.formGrid}>
          <div>
            <label style={styles.label}>Case Number</label>
            <input style={styles.input} name="caseNumber" value={formData.caseNumber} onChange={handleChange} placeholder="SA260137" />
          </div>
          <div>
            <label style={styles.label}>Judge / Court</label>
            <input style={styles.input} name="judgeName" value={formData.judgeName} onChange={handleChange} placeholder="Optional" />
          </div>
          <div>
            <label style={styles.label}>Defendant Name</label>
            <input style={styles.input} name="defendantName" value={formData.defendantName} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>DOB</label>
            <input style={styles.input} name="dob" value={formData.dob} onChange={handleChange} placeholder="00/00/0000" />
          </div>
          <div>
            <label style={styles.label}>Offense Date</label>
            <input style={styles.input} type="date" name="offenseDate" value={formData.offenseDate} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>Filing / Warrant Date</label>
            <input style={styles.input} type="date" name="filingDate" value={formData.filingDate} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>Hair</label>
            <input style={styles.input} name="hair" value={formData.hair} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>Eyes</label>
            <input style={styles.input} name="eyes" value={formData.eyes} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>Height</label>
            <input style={styles.input} name="height" value={formData.height} onChange={handleChange} placeholder="5'10&quot;" />
          </div>
          <div>
            <label style={styles.label}>Weight</label>
            <input style={styles.input} name="weight" value={formData.weight} onChange={handleChange} placeholder="180 lbs" />
          </div>
          <div>
            <label style={styles.label}>Race / Sex</label>
            <input style={styles.input} name="raceSex" value={formData.raceSex} onChange={handleChange} placeholder="White / Male" />
          </div>
          <div>
            <label style={styles.label}>DL #</label>
            <input style={styles.input} name="dl" value={formData.dl} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>SSN</label>
            <input style={styles.input} name="ssn" value={formData.ssn} onChange={handleChange} placeholder="XXX-XX-1234" />
          </div>
          <div>
            <label style={styles.label}>Alias</label>
            <input style={styles.input} name="alias" value={formData.alias} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>Alias DOB</label>
            <input style={styles.input} name="aliasDob" value={formData.aliasDob} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>Affiant Name</label>
            <input style={styles.input} name="affiantName" value={formData.affiantName} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>Affiant Phone</label>
            <input style={styles.input} name="affiantPhone" value={formData.affiantPhone} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>Pronoun for Affidavit</label>
            <select style={styles.input} name="pronoun" value={formData.pronoun} onChange={handleChange}>
              <option value="he">he</option>
              <option value="she">she</option>
              <option value="they">they</option>
              <option value="he/she">he/she</option>
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={styles.label}>Charges — one per line, max 10 for the warrant table</label>
            <textarea style={styles.textarea} name="charges" value={formData.charges} onChange={handleChange} placeholder={"18-1-111 Name of Charge\n18-3-204 Assault in the Third Degree"} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={styles.label}>Probable Cause Narrative (Affidavit Only)</label>
            <textarea style={{ ...styles.textarea, minHeight: 220 }} name="probableCause" value={formData.probableCause} onChange={handleChange} placeholder="Enter probable cause narrative for the affidavit..." />
          </div>
        </div>

        <div style={styles.topActions}>
          <button style={styles.primaryButton} type="button" onClick={handleGenerateDocx} disabled={generating}>
            {generating ? "Generating..." : "Download DOCX Packet (.zip)"}
          </button>
        </div>
      </form>
    </div>
  );
}


function SearchWarrantGeneratorModule({ currentUser }) {
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    caseNumber: "",
    judgeName: "",
    affiantName: currentUser?.displayName || currentUser?.username || "",
    affiantTitle: "Deputy",
    locationDescription: "",
    itemsToSeize: "",
    probableCause: "",
    filingDate: toDateInputValue(new Date()),
    furtherOrders: "1. This search warrant and its supporting affidavit be SEALED until completion of the investigation, or until further ordered by the Court.",
  });

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function requireFields() {
    const required = [
      ["affiantName", "Affiant name"],
      ["locationDescription", "Location description"],
      ["itemsToSeize", "Items to be searched/seized"],
      ["probableCause", "Probable cause narrative"],
      ["filingDate", "Filing / warrant date"],
    ];

    for (const [key, label] of required) {
      if (!safeStr(formData[key], "").trim()) {
        alert(`${label} is required.`);
        return false;
      }
    }
    return true;
  }

  async function handleGenerateDocx() {
    if (!requireFields()) return;

    try {
      setGenerating(true);
      const replacements = buildSearchWarrantReplacements(formData, currentUser);
      const baseName = `${sanitizeFilenamePart(formData.caseNumber || "search_warrant", "search_warrant")}_${sanitizeFilenamePart(formData.locationDescription.split("\n")[0] || "location", "location")}`;

      await generateSearchWarrantPacketZip(replacements, baseName);
    } catch (err) {
      alert(err.message || "Failed to generate search warrant packet.");
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div style={styles.sectionCard}>
      <h2 style={styles.h2}>Search Warrant Generator</h2>
      <p style={styles.muted}>
        Generates a search warrant affidavit and search warrant DOCX packet using the templates in public/Documents/search-warrant.
      </p>

      <form style={styles.form} onSubmit={(e) => e.preventDefault()}>
        <div style={styles.formGrid}>
          <div>
            <label style={styles.label}>Case Number</label>
            <input style={styles.input} name="caseNumber" value={formData.caseNumber} onChange={handleChange} placeholder="Optional" />
          </div>
          <div>
            <label style={styles.label}>Judge / Court</label>
            <input style={styles.input} name="judgeName" value={formData.judgeName} onChange={handleChange} placeholder="Optional" />
          </div>
          <div>
            <label style={styles.label}>Affiant Title</label>
            <input style={styles.input} name="affiantTitle" value={formData.affiantTitle} onChange={handleChange} placeholder="Deputy" />
          </div>
          <div>
            <label style={styles.label}>Affiant Name</label>
            <input style={styles.input} name="affiantName" value={formData.affiantName} onChange={handleChange} />
          </div>
          <div>
            <label style={styles.label}>Filing / Warrant Date</label>
            <input style={styles.input} type="date" name="filingDate" value={formData.filingDate} onChange={handleChange} />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={styles.label}>Place / Location Description</label>
            <textarea
              style={{ ...styles.textarea, minHeight: 180 }}
              name="locationDescription"
              value={formData.locationDescription}
              onChange={handleChange}
              placeholder={"*123 Main St, Center, CO, Saguache County, State of Colorado; also described as...\n*123 Main St. includes all garages, sheds, vehicles, safes, locked containers, outbuildings, and surrounding curtilage."}
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={styles.label}>Items to be Searched, Seized, Copied, Photographed and/or Examined</label>
            <textarea
              style={{ ...styles.textarea, minHeight: 180 }}
              name="itemsToSeize"
              value={formData.itemsToSeize}
              onChange={handleChange}
              placeholder={"1. Firearms, ammunition, safes, locked containers...\n2. Controlled substances, packaging materials, scales..."}
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={styles.label}>Probable Cause Narrative</label>
            <textarea
              style={{ ...styles.textarea, minHeight: 260 }}
              name="probableCause"
              value={formData.probableCause}
              onChange={handleChange}
              placeholder="Enter probable cause narrative..."
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={styles.label}>Further Orders</label>
            <textarea
              style={styles.textarea}
              name="furtherOrders"
              value={formData.furtherOrders}
              onChange={handleChange}
            />
          </div>
        </div>

        <div style={styles.topActions}>
          <button style={styles.primaryButton} type="button" onClick={handleGenerateDocx} disabled={generating}>
            {generating ? "Generating..." : "Download Search Warrant Packet (.zip)"}
          </button>
        </div>
      </form>
    </div>
  );
}


function DocumentsModule({ currentUser }) {
  const [documents, setDocuments] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(DOCUMENT_CATEGORIES[0]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [fileInputKey, setFileInputKey] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    category: DOCUMENT_CATEGORIES[0],
    title: "",
  });

  const canManage = canEditRecords(currentUser);

  async function refreshDocuments() {
    try {
      setLoading(true);
      const data = await apiFetch("/api/documents");
      setDocuments(normalizeListResponse(data));
    } catch (err) {
      alert(err.message || "Failed to load documents.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refreshDocuments(); }, []);

  const categoryCounts = useMemo(() => {
    const counts = Object.fromEntries(DOCUMENT_CATEGORIES.map((category) => [category, 0]));
    for (const doc of documents) {
      if (counts[doc.category] != null) counts[doc.category] += 1;
    }
    return counts;
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return [...documents]
      .filter((doc) => doc.category === selectedCategory)
      .filter((doc) => {
        if (!needle) return true;
        return [doc.title, doc.fileName, doc.uploadedByName, doc.category]
          .map((value) => safeStr(value, "").toLowerCase())
          .some((value) => value.includes(needle));
      })
      .sort((a, b) => sortByDateDesc(a.updatedAt || a.createdAt, b.updatedAt || b.createdAt));
  }, [documents, selectedCategory, search]);

  function handleFormChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function resetUploadForm(category = selectedCategory) {
    setFormData({ category, title: "" });
    setSelectedFile(null);
    setFileInputKey((v) => v + 1);
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    if (file && !formData.title.trim()) {
      const stripped = file.name.replace(/\.[^.]+$/, "");
      setFormData((prev) => ({ ...prev, title: stripped }));
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!canManage) return alert("Only Supervisors and Admins can upload document templates.");
    if (!formData.category || !formData.title.trim() || !selectedFile) {
      return alert("Please choose a category, enter a title, and select a file.");
    }
    if (selectedFile.size > 5 * 1024 * 1024) {
      return alert("Keep templates under 5 MB for this version.");
    }
    try {
      setUploading(true);
      const dataUrl = await fileToDataUrl(selectedFile);
      await apiFetch("/api/documents", {
        method: "POST",
        body: JSON.stringify({
          category: formData.category,
          title: formData.title.trim(),
          fileName: selectedFile.name,
          contentType: selectedFile.type || "application/octet-stream",
          fileSize: selectedFile.size,
          fileBase64: dataUrlToBase64(dataUrl),
        }),
      });
      alert("Template uploaded.");
      setSelectedCategory(formData.category);
      resetUploadForm(formData.category);
      await refreshDocuments();
    } catch (err) {
      alert(err.message || "Failed to upload template.");
    } finally {
      setUploading(false);
    }
  }

  async function handleDownload(doc) {
    try {
      setDownloadingId(doc.id);
      const data = await apiFetch(`/api/documents?id=${encodeURIComponent(doc.id)}&download=1`);
      const blob = base64ToBlob(data.fileBase64, data.contentType || doc.contentType);
      triggerBlobDownload(blob, data.fileName || doc.fileName || `${safeStr(doc.title, "template")}.bin`);
    } catch (err) {
      alert(err.message || "Failed to download document.");
    } finally {
      setDownloadingId("");
    }
  }

  async function handleDelete(doc) {
    if (!canManage) return;
    if (!window.confirm(`Delete template "${doc.title}"?`)) return;
    try {
      setDeletingId(doc.id);
      await apiFetch(`/api/documents?id=${encodeURIComponent(doc.id)}`, { method: "DELETE" });
      await refreshDocuments();
    } catch (err) {
      alert(err.message || "Failed to delete template.");
    } finally {
      setDeletingId("");
    }
  }

  return (
    <div style={styles.sectionCard}>
      <h2 style={styles.h2}>Documents Module</h2>
      <p style={styles.muted}>Shared downloadable template library. Supervisors and Admins can upload and remove templates. Deputies can download them.</p>

      <div style={styles.moduleGrid}>
        {DOCUMENT_CATEGORIES.map((category) => (
          <div key={category} style={styles.moduleCard}>
            <div style={styles.moduleCardHeader}>
              <h3 style={styles.h3}>{category}</h3>
              <span style={styles.permissionPill}>{categoryCounts[category] || 0}</span>
            </div>
            <div style={styles.topActions}>
              <button style={styles.primaryButton} type="button" onClick={() => setSelectedCategory(category)}>View</button>
              {canManage && (
                <button style={styles.secondaryButton} type="button" onClick={() => {
                  setSelectedCategory(category);
                  setFormData((prev) => ({ ...prev, category }));
                  window.scrollTo(0, 0);
                }}>Upload Here</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {canManage && (
        <div style={{ ...styles.sectionCard, marginTop: "18px" }}>
          <h3 style={styles.h3}>Upload Template</h3>
          <form onSubmit={handleUpload} style={styles.form}>
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Category</label>
                <select style={styles.input} name="category" value={formData.category} onChange={handleFormChange}>
                  {DOCUMENT_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Template Title</label>
                <input style={styles.input} name="title" value={formData.title} onChange={handleFormChange} placeholder="Search Warrant Template v1" />
              </div>
            </div>
            <div>
              <label style={styles.label}>File</label>
              <input key={fileInputKey} style={styles.input} type="file" onChange={handleFileChange} />
              <div style={styles.smallText}>
                {selectedFile ? `Selected: ${selectedFile.name} (${formatBytes(selectedFile.size)})` : "Choose a .docx, .pdf, .xlsx, or other template file."}
              </div>
            </div>
            <div style={styles.topActions}>
              <button style={styles.primaryButton} type="submit" disabled={uploading}>{uploading ? "Uploading..." : "Upload Template"}</button>
              <button style={styles.secondaryButton} type="button" onClick={() => resetUploadForm(formData.category)}>Clear</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ ...styles.sectionCard, marginTop: "18px", padding: "16px", background: "#f8fafc" }}>
        <div style={styles.topActions}>
          <div style={{ minWidth: "240px", flex: "1 1 240px" }}>
            <label style={styles.label}>Viewing Category</label>
            <select style={styles.input} value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
              {DOCUMENT_CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
          </div>
          <div style={{ minWidth: "240px", flex: "1 1 240px" }}>
            <label style={styles.label}>Search</label>
            <input style={styles.input} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, file name, or uploader" />
          </div>
        </div>

        <div style={styles.vacationList}>
          {loading ? (
            <div style={styles.vacationCard}>Loading templates...</div>
          ) : filteredDocuments.length === 0 ? (
            <div style={styles.vacationCard}>No templates found in this category.</div>
          ) : filteredDocuments.map((doc) => (
            <div key={doc.id} style={styles.vacationCard}>
              <div style={styles.vacationCardHeader}>
                <div>
                  <div style={{ fontWeight: 700 }}>{doc.title}</div>
                  <div style={styles.smallText}>{safeStr(doc.fileName, "file")} • {formatBytes(doc.fileSize)} • Uploaded by {safeStr(doc.uploadedByName, "Unknown")} • {formatDateTime(doc.updatedAt || doc.createdAt)}</div>
                </div>
                <span style={styles.permissionPill}>{doc.category}</span>
              </div>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}><span style={styles.detailLabel}>File Name:</span> {safeStr(doc.fileName, "—")}</div>
                <div style={styles.detailItem}><span style={styles.detailLabel}>Type:</span> {safeStr(doc.contentType, "—")}</div>
              </div>
              <div style={{ ...styles.topActions, marginTop: 12, marginBottom: 0 }}>
                <button style={styles.primaryButton} type="button" onClick={() => handleDownload(doc)} disabled={downloadingId === doc.id}>{downloadingId === doc.id ? "Downloading..." : "Download"}</button>
                {canManage && <button style={styles.dangerButton} type="button" onClick={() => handleDelete(doc)} disabled={deletingId === doc.id}>{deletingId === doc.id ? "Deleting..." : "Delete"}</button>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function ChangePasswordModule() {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);

  function handleFormChange(e) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      return alert("Please complete all password fields.");
    }

    if (formData.newPassword !== formData.confirmPassword) {
      return alert("New password and confirmation do not match.");
    }

    try {
      setLoading(true);
      const data = await apiFetch("/api/change-password", {
        method: "POST",
        body: JSON.stringify(formData),
      });
      alert(data?.message || "Password changed successfully.");
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      alert(err.message || "Failed to change password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.sectionCard}>
      <h2 style={styles.h2}>Change Password</h2>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formGrid}>
          <div>
            <label style={styles.label}>Current Password</label>
            <input
              style={styles.input}
              type="password"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleFormChange}
            />
          </div>
          <div>
            <label style={styles.label}>New Password</label>
            <input
              style={styles.input}
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleFormChange}
            />
          </div>
          <div>
            <label style={styles.label}>Confirm New Password</label>
            <input
              style={styles.input}
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleFormChange}
            />
          </div>
        </div>
        <div style={styles.topActions}>
          <button style={styles.primaryButton} disabled={loading}>
            {loading ? "Updating..." : "Update Password"}
          </button>
        </div>
      </form>
    </div>
  );

}

function BeginningMilesWidget({ currentUser }) {
  const [miles, setMiles] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    const n = Number(miles);

    if (!Number.isFinite(n) || n < 0) {
      return alert("Enter a valid mileage number.");
    }

    try {
      setSaving(true);
      await apiFetch("/api/mileage-log", {
        method: "POST",
        body: JSON.stringify({ miles: n }),
      });
      setMiles("");
      alert("Beginning miles saved.");
    } catch (err) {
      alert(err.message || "Failed to save mileage.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        ...styles.sectionCard,
        marginBottom: 16,
        maxWidth: 370,
        padding: 14,
      }}
    >
      <h3 style={{ ...styles.h3, margin: 0, marginBottom: 4 }}>Beginning Miles</h3>
      <div style={{ ...styles.muted, fontSize: 12, lineHeight: 1.3, marginBottom: 10 }}>
        Logged by user with automatic timestamp.
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          gap: 14,
          alignItems: "end",
          flexWrap: "nowrap",
        }}
      >
        <div style={{ flex: "1 1 190px", minWidth: 180, maxWidth: 240 }}>
          <label style={{ ...styles.label, display: "block", marginBottom: 4 }}>Miles</label>
          <input
            style={{
              ...styles.input,
              width: "100%",
              margin: 0,
              boxSizing: "border-box",
            }}
            type="number"
            min="0"
            step="1"
            value={miles}
            onChange={(e) => setMiles(e.target.value)}
            placeholder="Enter miles"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          style={{
            background: "#16213e",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 16px",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1,
            whiteSpace: "nowrap",
            width: "auto",
            minWidth: "unset",
            maxWidth: "fit-content",
            flex: "0 0 auto",
            alignSelf: "end",
            marginLeft: 4,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </form>
    </div>
  );
}


function FuelDashboardWidget({ currentUser, onSaved }) {
  const [gallons, setGallons] = useState("");
  const [location, setLocation] = useState("");
  const [saving, setSaving] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    const n = Number(gallons);
    if (!Number.isFinite(n) || n <= 0) return alert("Gallons must be greater than zero.");
    if (!["north", "south"].includes(location)) return alert("Select North or South fuel location.");

    try {
      setSaving(true);
      const now = new Date().toISOString();
      const payload = {
        id: uid("fuel"),
        gallons: Number(n.toFixed(2)),
        location,
        submittedBy: currentUser?.displayName || currentUser?.username || "Unknown User",
        createdAt: now,
        updatedAt: now,
      };
      saveFuelEntries([payload, ...loadFuelEntries()]);
      setGallons("");
      setLocation("");
      onSaved?.();
      alert("Fuel entry saved.");
    } catch (err) {
      alert(err.message || "Failed to save fuel entry.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ ...styles.sectionCard, marginBottom: 16, padding: 14 }}>
      <h3 style={{ ...styles.h3, margin: 0, marginBottom: 4 }}>Fuel Entry</h3>
      <div style={{ ...styles.muted, fontSize: 12, lineHeight: 1.3, marginBottom: 10 }}>
        Quick fuel log with automatic timestamp.
      </div>
      <form onSubmit={handleSubmit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }}>
        <div>
          <label style={{ ...styles.label, display: "block", marginBottom: 4 }}>Gallons</label>
          <input style={{ ...styles.input, margin: 0 }} type="number" min="0" step="0.01" value={gallons} onChange={(e) => setGallons(e.target.value)} placeholder="0.00" />
        </div>
        <div>
          <label style={{ ...styles.label, display: "block", marginBottom: 4 }}>Location</label>
          <select style={{ ...styles.input, margin: 0 }} value={location} onChange={(e) => setLocation(e.target.value)}>
            <option value="">Select</option>
            <option value="north">North</option>
            <option value="south">South</option>
          </select>
        </div>
        <button type="submit" disabled={saving} style={{ ...styles.primaryButton, whiteSpace: "nowrap", opacity: saving ? 0.7 : 1 }}>
          {saving ? "Saving..." : "Save"}
        </button>
      </form>
    </div>
  );
}

function WarrantCenterModule({ currentUser }) {
  const [active, setActive] = useState("arrest");
  return (
    <section>
      <div style={styles.sectionCard}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <h2 style={styles.h2}>Warrant Center</h2>
            <p style={styles.muted}>Arrest and search warrant generators consolidated into one document workspace.</p>
          </div>
          <div style={styles.segmentedControl}>
            <button type="button" style={{ ...styles.segmentButton, ...(active === "arrest" ? styles.segmentButtonActive : {}) }} onClick={() => setActive("arrest")}>Arrest Warrant</button>
            <button type="button" style={{ ...styles.segmentButton, ...(active === "search" ? styles.segmentButtonActive : {}) }} onClick={() => setActive("search")}>Search Warrant</button>
          </div>
        </div>
      </div>
      {active === "arrest" ? <ArrestWarrantGeneratorModule currentUser={currentUser} /> : <SearchWarrantGeneratorModule currentUser={currentUser} />}
    </section>
  );
}

function AdminReportsModule({ currentUser }) {
  const [activeReport, setActiveReport] = useState("fuel");

  const [fuelEntries, setFuelEntries] = useState([]);
  const [fuelFilterMonth, setFuelFilterMonth] = useState("");
  const [fuelFilterDate, setFuelFilterDate] = useState("");
  const [fuelFilterUser, setFuelFilterUser] = useState("");
  const [fuelFilterLocation, setFuelFilterLocation] = useState("");

  const [mileageEntries, setMileageEntries] = useState([]);
  const [mileageLoading, setMileageLoading] = useState(false);
  const [mileageFilterUser, setMileageFilterUser] = useState("");
  const [mileageFilterDate, setMileageFilterDate] = useState("");

  function loadFuelReport() {
    setFuelEntries(loadFuelEntries());
  }

  async function loadMileageReport() {
    try {
      setMileageLoading(true);
      const data = await apiFetch("/api/mileage-log");
      setMileageEntries(normalizeListResponse(data));
    } catch (err) {
      alert(err.message || "Failed to load mileage report.");
    } finally {
      setMileageLoading(false);
    }
  }

  useEffect(() => {
    loadFuelReport();
    loadMileageReport();
  }, []);

  const sortedFuelEntries = useMemo(
    () => [...fuelEntries].sort((a, b) => sortByDateDesc(a.createdAt, b.createdAt)),
    [fuelEntries]
  );

  const visibleFuelEntries = useMemo(() => {
    return sortedFuelEntries.filter((entry) => {
      if (fuelFilterMonth) {
        const mk = monthKeyFromDate((entry.createdAt || "").slice(0, 10));
        if (mk !== fuelFilterMonth) return false;
      }
      if (fuelFilterDate && safeStr((entry.createdAt || "").slice(0, 10), "") !== fuelFilterDate) return false;
      if (fuelFilterUser && safeStr(entry.submittedBy, "") !== fuelFilterUser) return false;
      if (fuelFilterLocation && safeStr(entry.location, "") !== fuelFilterLocation) return false;
      return true;
    });
  }, [sortedFuelEntries, fuelFilterMonth, fuelFilterDate, fuelFilterUser, fuelFilterLocation]);

  const fuelUserOptions = useMemo(
    () => Array.from(new Set(sortedFuelEntries.map((entry) => safeStr(entry.submittedBy, "")).filter(Boolean))).sort(),
    [sortedFuelEntries]
  );

  const totalGallons = useMemo(
    () => visibleFuelEntries.reduce((sum, entry) => sum + (safeNum(entry.gallons) || 0), 0),
    [visibleFuelEntries]
  );

  const sortedMileageEntries = useMemo(
    () => [...mileageEntries].sort((a, b) => sortByDateDesc(a.createdAt, b.createdAt)),
    [mileageEntries]
  );

  const visibleMileageEntries = useMemo(() => {
    return sortedMileageEntries.filter((entry) => {
      if (mileageFilterUser && safeStr(entry.submittedByName, "") !== mileageFilterUser) return false;
      if (mileageFilterDate && safeStr((entry.createdAt || "").slice(0, 10), "") !== mileageFilterDate) return false;
      return true;
    });
  }, [sortedMileageEntries, mileageFilterUser, mileageFilterDate]);

  const mileageUserOptions = useMemo(
    () => Array.from(new Set(sortedMileageEntries.map((entry) => safeStr(entry.submittedByName, "")).filter(Boolean))).sort(),
    [sortedMileageEntries]
  );

  function exportFuelCsv() {
    const header = ["Submitted At", "Date", "Submitted By", "Gallons", "Location"];
    const rows = visibleFuelEntries.map((entry) => [
      safeStr(entry.createdAt, ""),
      safeStr((entry.createdAt || "").slice(0, 10), ""),
      safeStr(entry.submittedBy, ""),
      safeStr(entry.gallons, ""),
      safeStr(entry.location, ""),
    ]);

    const csv = [header, ...rows]
      .map((row) => row.map((cell) => {
        const value = safeStr(cell, "");
        return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
      }).join(","))
      .join("\n");

    downloadTextFile(`fuel-report-${fuelFilterMonth || fuelFilterDate || "all"}.csv`, csv);
  }

  function exportMileageCsv() {
    const header = ["Timestamp", "Date", "User", "Miles"];
    const rows = visibleMileageEntries.map((entry) => [
      safeStr(entry.createdAt, ""),
      safeStr((entry.createdAt || "").slice(0, 10), ""),
      safeStr(entry.submittedByName, ""),
      safeStr(entry.miles, ""),
    ]);

    const csv = [header, ...rows]
      .map((row) => row.map((cell) => {
        const value = safeStr(cell, "");
        return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
      }).join(","))
      .join("\n");

    downloadTextFile(`mileage-report-${mileageFilterDate || "all"}.csv`, csv);
  }

  if (!isAdmin(currentUser)) {
    return (
      <div style={styles.sectionCard}>
        <h2 style={styles.h2}>Admin Reports</h2>
        <p style={styles.muted}>Admin access required.</p>
      </div>
    );
  }

  return (
    <div style={styles.sectionCard}>
      <h2 style={styles.h2}>Admin Reports</h2>
      <p style={styles.muted}>Generate mileage and fuel reports without cluttering the dashboard.</p>

      <div style={styles.jurisRow}>
        <button
          type="button"
          style={{ ...styles.filterButton, background: activeReport === "fuel" ? "#111" : "#fff", color: activeReport === "fuel" ? "#fff" : "#111" }}
          onClick={() => setActiveReport("fuel")}
        >
          Fuel Reports
        </button>
        <button
          type="button"
          style={{ ...styles.filterButton, background: activeReport === "mileage" ? "#111" : "#fff", color: activeReport === "mileage" ? "#fff" : "#111" }}
          onClick={() => setActiveReport("mileage")}
        >
          Mileage Reports
        </button>
      </div>

      {activeReport === "fuel" && (
        <div style={{ marginTop: 16 }}>
          <div style={styles.topActions}>
            <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
              <label style={styles.label}>Filter by Month</label>
              <input style={styles.input} type="month" value={fuelFilterMonth} onChange={(e) => setFuelFilterMonth(e.target.value)} />
            </div>
            <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
              <label style={styles.label}>Filter by Exact Date</label>
              <input style={styles.input} type="date" value={fuelFilterDate} onChange={(e) => setFuelFilterDate(e.target.value)} />
            </div>
            <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
              <label style={styles.label}>Filter by User</label>
              <select style={styles.input} value={fuelFilterUser} onChange={(e) => setFuelFilterUser(e.target.value)}>
                <option value="">All Users</option>
                {fuelUserOptions.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>
            <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
              <label style={styles.label}>Filter by Location</label>
              <select style={styles.input} value={fuelFilterLocation} onChange={(e) => setFuelFilterLocation(e.target.value)}>
                <option value="">All Locations</option>
                <option value="north">North</option>
                <option value="south">South</option>
              </select>
            </div>
          </div>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Visible Fuel Entries</div>
              <div style={styles.summaryValue}>{visibleFuelEntries.length}</div>
            </div>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Visible Gallons</div>
              <div style={styles.summaryValue}>{totalGallons.toFixed(2)}</div>
            </div>
          </div>

          <div style={styles.topActions}>
            <button type="button" style={styles.primaryButton} onClick={exportFuelCsv}>Export Fuel CSV</button>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => {
                setFuelFilterMonth("");
                setFuelFilterDate("");
                setFuelFilterUser("");
                setFuelFilterLocation("");
              }}
            >
              Clear Filters
            </button>
            <button type="button" style={styles.secondaryButton} onClick={loadFuelReport}>Refresh</button>
          </div>

          <div style={styles.userTableWrapper}>
            <table style={styles.userTable}>
              <thead>
                <tr>
                  <th style={styles.userTableTh}>Submitted At</th>
                  <th style={styles.userTableTh}>User</th>
                  <th style={styles.userTableTh}>Gallons</th>
                  <th style={styles.userTableTh}>Location</th>
                </tr>
              </thead>
              <tbody>
                {visibleFuelEntries.length === 0 ? (
                  <tr><td style={styles.userTableTd} colSpan={4}>No fuel entries found.</td></tr>
                ) : visibleFuelEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td style={styles.userTableTd}>{formatDateTime(entry.createdAt)}</td>
                    <td style={styles.userTableTd}>{safeStr(entry.submittedBy, "—")}</td>
                    <td style={styles.userTableTd}>{safeStr(entry.gallons, "—")}</td>
                    <td style={styles.userTableTd}>{safeStr(entry.location, "—")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeReport === "mileage" && (
        <div style={{ marginTop: 16 }}>
          <div style={styles.topActions}>
            <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
              <label style={styles.label}>Filter by User</label>
              <select style={styles.input} value={mileageFilterUser} onChange={(e) => setMileageFilterUser(e.target.value)}>
                <option value="">All Users</option>
                {mileageUserOptions.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </div>
            <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
              <label style={styles.label}>Filter by Date</label>
              <input style={styles.input} type="date" value={mileageFilterDate} onChange={(e) => setMileageFilterDate(e.target.value)} />
            </div>
          </div>

          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <div style={styles.summaryLabel}>Visible Mileage Entries</div>
              <div style={styles.summaryValue}>{visibleMileageEntries.length}</div>
            </div>
          </div>

          <div style={styles.topActions}>
            <button type="button" style={styles.primaryButton} onClick={exportMileageCsv}>Export Mileage CSV</button>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => {
                setMileageFilterUser("");
                setMileageFilterDate("");
              }}
            >
              Clear Filters
            </button>
            <button type="button" style={styles.secondaryButton} onClick={loadMileageReport}>Refresh</button>
          </div>

          <div style={styles.userTableWrapper}>
            <table style={styles.userTable}>
              <thead>
                <tr>
                  <th style={styles.userTableTh}>Timestamp</th>
                  <th style={styles.userTableTh}>User</th>
                  <th style={styles.userTableTh}>Miles</th>
                </tr>
              </thead>
              <tbody>
                {mileageLoading ? (
                  <tr><td style={styles.userTableTd} colSpan={3}>Loading...</td></tr>
                ) : visibleMileageEntries.length === 0 ? (
                  <tr><td style={styles.userTableTd} colSpan={3}>No mileage entries found.</td></tr>
                ) : visibleMileageEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td style={styles.userTableTd}>{formatDateTime(entry.createdAt)}</td>
                    <td style={styles.userTableTd}>{safeStr(entry.submittedByName, "—")}</td>
                    <td style={styles.userTableTd}>{safeStr(entry.miles, "—")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}



function PersistentNotepad({ currentUser }) {
  const userKey = currentUser?.id || currentUser?.username || "default";
  const noteKey = `${NOTEPAD_STORAGE_KEY}_${userKey}`;
  const minimizedKey = `${NOTEPAD_STORAGE_KEY}_header_minimized_${userKey}`;
  const [note, setNote] = useState("");
  const [minimized, setMinimized] = useState(true);

  useEffect(() => {
    try {
      setNote(localStorage.getItem(noteKey) || "");
      const savedMinimized = localStorage.getItem(minimizedKey);
      setMinimized(savedMinimized == null ? true : savedMinimized === "true");
    } catch {
      setNote("");
      setMinimized(true);
    }
  }, [noteKey, minimizedKey]);

  function handleNoteChange(e) {
    const value = e.target.value;
    setNote(value);
    try { localStorage.setItem(noteKey, value); } catch {}
  }

  function toggleMinimized() {
    const next = !minimized;
    setMinimized(next);
    try { localStorage.setItem(minimizedKey, String(next)); } catch {}
  }

  function clearNote() {
    if (!window.confirm("Clear your notepad?")) return;
    setNote("");
    try { localStorage.setItem(noteKey, ""); } catch {}
  }

  return (
    <div style={styles.headerNotepadWrap}>
      <div style={styles.headerNotepadTop}>
        <span style={styles.headerNotepadTitle}>Notepad</span>
        <div style={{ display: "flex", gap: 6 }}>
          {!minimized && (
            <button type="button" style={styles.headerNotepadButton} onClick={clearNote}>Clear</button>
          )}
          <button type="button" style={styles.headerNotepadButton} onClick={toggleMinimized}>
            {minimized ? "Open" : "Min"}
          </button>
        </div>
      </div>
      {!minimized && (
        <textarea
          style={styles.headerNotepadTextarea}
          value={note}
          onChange={handleNoteChange}
          placeholder="Quick notes..."
        />
      )}
    </div>
  );
}

function AdminModule({ onOpenReports, onOpenDashboard, onOpenUsers, onBack }) {
  return (
    <section>
      <div style={styles.topActions}>
        <button style={styles.secondaryButton} onClick={onBack}>← Back to Home</button>
      </div>
      <div style={styles.sectionCard}>
        <h2 style={styles.h2}>Admin</h2>
        <p style={styles.muted}>Admin tools, reports, user management, and dashboard activity.</p>
        <div style={styles.moduleGrid}>
          <div style={styles.moduleCard}>
            <h3 style={styles.h3}>Admin Reports</h3>
            <p style={styles.muted}>Generate and export fuel and mileage reports.</p>
            <button style={styles.primaryButton} onClick={onOpenReports}>Open</button>
          </div>
          <div style={styles.moduleCard}>
            <h3 style={styles.h3}>Admin Dashboard</h3>
            <p style={styles.muted}>View usage, login activity, audit activity, and pending approvals.</p>
            <button style={styles.primaryButton} onClick={onOpenDashboard}>Open</button>
          </div>
          <div style={styles.moduleCard}>
            <h3 style={styles.h3}>User Management</h3>
            <p style={styles.muted}>Approve users, change roles, disable accounts, and manage access.</p>
            <button style={styles.primaryButton} onClick={onOpenUsers}>Open</button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(undefined);
  const [currentScreen, setCurrentScreen] = useState("home");
  const [alertRefreshKey, setAlertRefreshKey] = useState(0);
  const [vacationItems, setVacationItems] = useState([]);
  const [redTaggedItems, setRedTaggedItems] = useState([]);
  const [passOnItems, setPassOnItems] = useState([]);

  async function loadSession() {
    try { const data = await apiFetch("/api/me"); setCurrentUser(normalizeUserResponse(data) || null); }
    catch { setCurrentUser(null); }
  }
  async function refreshAlertData() {
    if (!currentUser?.id) return;
    try {
      const [vacationData, redData, passOnData] = await Promise.all([
        apiFetch("/api/security-checks"),
        apiFetch("/api/red-tagged"),
        apiFetch("/api/pass-ons"),
      ]);
      setVacationItems(purgeExpiredVacationChecks(normalizeListResponse(vacationData)));
      setRedTaggedItems(normalizeListResponse(redData));
      setPassOnItems(purgeExpiredPassOns(normalizeListResponse(passOnData)));
    } catch (err) { console.error(err); }
  }
  useEffect(() => { loadSession(); }, []);
  useEffect(() => {
    if (currentUser?.id) refreshAlertData();
    else { setVacationItems([]); setRedTaggedItems([]); setPassOnItems([]); }
  }, [currentUser?.id, alertRefreshKey]);
  function handleLogin(user) { setCurrentUser(user); setCurrentScreen("home"); setAlertRefreshKey((v)=>v+1); }
  async function handleLogout() { try { await apiFetch("/api/logout", { method:"POST" }); } catch {} setCurrentUser(null); setCurrentScreen("home"); setAlertRefreshKey((v)=>v+1); }
  function handleNavigate(screen) {
    if (currentUser?.id && ["security","redtagged"].includes(screen)) markModuleSeen(currentUser.id, screen);
    setCurrentScreen(screen);
    setAlertRefreshKey((v)=>v+1);
    window.scrollTo(0,0);
  }
  function handleOpenPassOnType(type) { if (currentUser?.id) markModuleSeen(currentUser.id, getPassOnSeenKey(type)); setCurrentScreen(`passon:${type}`); setAlertRefreshKey((v)=>v+1); }
  const activePassOnType = currentScreen.startsWith("passon:") ? currentScreen.slice("passon:".length) : "";
  const vacationUnreadCount = useMemo(() => getUnreadModuleCount(currentUser?.id, "security", vacationItems), [currentUser, alertRefreshKey, vacationItems]);
  const redTaggedUnreadCount = useMemo(() => getUnreadModuleCount(currentUser?.id, "redtagged", redTaggedItems), [currentUser, alertRefreshKey, redTaggedItems]);
  const passOnUnreadCount = useMemo(() => {
    if (!currentUser?.id) return 0;
    return PASS_ON_TYPES.reduce((total, type) => total + getUnreadModuleCount(currentUser.id, getPassOnSeenKey(type), passOnItems.filter((x) => x.submissionType === type)), 0);
  }, [currentUser, alertRefreshKey, passOnItems]);

  const fuelEntriesToday = useMemo(() => {
    const today = toDateInputValue(new Date());
    return loadFuelEntries().filter((entry) => safeStr(entry.createdAt, "").slice(0, 10) === today).length;
  }, [alertRefreshKey]);

  if (currentUser === undefined) return <div style={styles.authWrapper}><div style={styles.authCard}><h1 style={styles.authTitle}>PatrolOps</h1><p style={styles.authSubtitle}>Checking session...</p></div></div>;
  if (!currentUser) return <AuthScreen onLogin={handleLogin} />;

  const navGroups = [
    { label: "COMMAND", items: [
      { screen: "home", label: "Dashboard", icon: "▣" },
      { screen: "traffic", label: "Traffic Module", icon: "≡" },
    ]},
    { label: "PATROL TOOLS", items: [
      { screen: "passon", label: "Pass Ons", icon: "↗", badge: passOnUnreadCount ? "New" : "" },
      { screen: "security", label: "Security Checks", icon: "□", badge: vacationUnreadCount || "" },
      { screen: "redtagged", label: "Red Tagged", icon: "◇", badge: redTaggedUnreadCount || "" },
    ]},
    { label: "DOCUMENTS", items: [
      { screen: "documents", label: "Documents", icon: "▤" },
      { screen: "warrants", label: "Warrant Center", icon: "⚖" },
    ]},
    { label: "REPORTING", items: [
      { screen: "fuel", label: "Fuel Logs", icon: "⛽" },
      ...(isAdmin(currentUser) ? [{ screen: "admin-reports", label: "Admin Reports", icon: "▧" }] : []),
    ]},
    { label: "SYSTEM", items: [
      ...(isAdmin(currentUser) ? [{ screen: "admin", label: "Admin", icon: "⚙" }] : []),
      { screen: "change-password", label: "Change Password", icon: "◉" },
    ]},
  ];

  function isActiveNav(screen) {
    if (screen === "passon") return currentScreen === "passon" || currentScreen === "passon-all" || currentScreen.startsWith("passon:");
    if (screen === "warrants") return currentScreen === "warrants" || currentScreen === "arrest-warrant-generator" || currentScreen === "search-warrant-generator";
    if (screen === "admin") return currentScreen === "admin" || currentScreen === "admin-dashboard" || currentScreen === "users";
    return currentScreen === screen;
  }

  function renderNavButton(item) {
    const active = isActiveNav(item.screen);
    return (
      <button key={item.screen} type="button" style={{ ...styles.sidebarButton, ...(active ? styles.sidebarButtonActive : {}) }} onClick={() => handleNavigate(item.screen)}>
        <span style={styles.sidebarIcon}>{item.icon}</span>
        <span style={styles.sidebarLabel}>{item.label}</span>
        {item.badge ? <span style={styles.sidebarBadge}>{item.badge}</span> : null}
      </button>
    );
  }

  function renderPageTitle(title, subtitle) {
    return (
      <div style={styles.pageTitleBlock}>
        <h2 style={styles.pageTitle}>{title}</h2>
        {subtitle ? <p style={styles.pageSubtitle}>{subtitle}</p> : null}
      </div>
    );
  }

  function renderDashboard() {
    const statCards = [
      { label: "Security Checks", value: vacationItems.length, note: vacationUnreadCount ? `${vacationUnreadCount} new` : "active", screen: "security" },
      { label: "Pass Ons", value: passOnItems.length, note: passOnUnreadCount ? "new alerts" : "current", screen: "passon" },
      { label: "Red Tagged", value: redTaggedItems.length, note: redTaggedUnreadCount ? `${redTaggedUnreadCount} new` : "vehicles", screen: "redtagged" },
      { label: "Fuel Today", value: fuelEntriesToday, note: "entries", screen: "fuel" },
    ];

    return (
      <section>
        {renderPageTitle("Command Dashboard", "Quick patrol entries, shift tools, and document generation in one command-style workspace.")}
        <div style={styles.commandGrid}>
          {statCards.map((card) => (
            <button key={card.label} type="button" style={styles.commandCard} onClick={() => handleNavigate(card.screen)}>
              <div style={styles.commandCardLabel}>{card.label}</div>
              <div style={styles.commandCardValue}>{card.value}</div>
              <div style={styles.commandCardNote}>{card.note}</div>
            </button>
          ))}
        </div>

        <div style={styles.dashboardTwoColumn}>
          <div>
            <div style={styles.sectionCard}>
              <h3 style={styles.h3}>Shift Quick Entry</h3>
              <p style={styles.muted}>Mileage and fuel are kept on the dashboard for fast patrol use.</p>
            </div>
            <div style={styles.quickEntryGrid}>
              <BeginningMilesWidget currentUser={currentUser} />
              <FuelDashboardWidget currentUser={currentUser} onSaved={() => setAlertRefreshKey((v) => v + 1)} />
            </div>
          </div>

          <div style={styles.sectionCard}>
            <h3 style={styles.h3}>Primary Work Centers</h3>
            <div style={styles.actionList}>
              <button style={styles.actionRow} onClick={() => handleNavigate("passon")}><span>Pass Ons</span><small>Shift notes, BOLOs, officer safety, and briefings.</small></button>
              <button style={styles.actionRow} onClick={() => handleNavigate("warrants")}><span>Warrant Center</span><small>Arrest and search warrant packet generators.</small></button>
              <button style={styles.actionRow} onClick={() => handleNavigate("documents")}><span>Documents</span><small>Agency templates and uploaded reference files.</small></button>
              {isAdmin(currentUser) && <button style={styles.actionRow} onClick={() => handleNavigate("admin")}><span>Admin</span><small>Reports, user approvals, and system activity.</small></button>}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <div style={styles.headerInnerWide}>
          <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
            <img src="/patrolops-logo-transparent.png" alt="PatrolOps Logo" style={{ height:"52px", width:"52px", objectFit:"contain" }} />
            <div>
              <div style={styles.headerTitle}>PatrolOps</div>
              <div style={styles.headerUser}>Patrol Operations Platform • {currentUser.displayName} ({currentUser.role})</div>
            </div>
          </div>
          <div style={styles.headerRightTools}>
            <PersistentNotepad currentUser={currentUser} />
            <button style={styles.secondaryButton} onClick={handleLogout}>Logout</button>
          </div>
        </div>
      </header>

      <div style={styles.shell}>
        <aside style={styles.sidebar}>
          <div style={styles.sidebarBrand}>PATROLOPS</div>
          <div style={styles.sidebarUser}>{currentUser.displayName || currentUser.username}<br/><span>{currentUser.role}</span></div>
          {navGroups.map((group) => (
            <div key={group.label} style={styles.sidebarGroup}>
              <div style={styles.sidebarGroupTitle}>{group.label}</div>
              {group.items.map(renderNavButton)}
            </div>
          ))}
        </aside>

        <main style={styles.contentArea}>
          {currentScreen === "home" && renderDashboard()}
          {currentScreen === "traffic" && <section>{renderPageTitle("Traffic Module", "Search municipal, county, and state traffic references.")}<div style={styles.sectionCard}><TrafficModule /></div></section>}
          {currentScreen === "security" && <section>{renderPageTitle("Security Checks", "Active security checks and new submissions.")}<VacationHouseCheck currentUser={currentUser} refreshSignal={alertRefreshKey} onDataChange={()=>setAlertRefreshKey((v)=>v+1)} /></section>}
          {currentScreen === "redtagged" && <section>{renderPageTitle("Red Tagged Vehicles", "Track tagged vehicles and removal timelines.")}<RedTaggedModule currentUser={currentUser} refreshSignal={alertRefreshKey} onDataChange={()=>setAlertRefreshKey((v)=>v+1)} /></section>}
          {currentScreen === "passon" && <PassOnHome currentUser={currentUser} passOnItems={passOnItems} onOpenType={handleOpenPassOnType} onOpenAll={()=>handleNavigate("passon-all")} onBack={()=>handleNavigate("home")} />}
          {currentScreen === "passon-all" && isAdmin(currentUser) && <section>{renderPageTitle("All Pass Ons", "Administrative view of all pass-on entries.")}<PassOnModule currentUser={currentUser} refreshSignal={alertRefreshKey} onDataChange={()=>setAlertRefreshKey((v)=>v+1)} showAll /></section>}
          {activePassOnType && <section>{renderPageTitle(activePassOnType, "Pass-on entries for the selected category.")}<PassOnModule currentUser={currentUser} refreshSignal={alertRefreshKey} onDataChange={()=>setAlertRefreshKey((v)=>v+1)} activeType={activePassOnType} /></section>}
          {currentScreen === "fuel" && <section>{renderPageTitle("Fuel Logs", "Fuel entry history and corrections.")}<FuelModule currentUser={currentUser} /></section>}
          {currentScreen === "documents" && <section>{renderPageTitle("Documents", "Upload, categorize, and retrieve agency documents.")}<DocumentsModule currentUser={currentUser} /></section>}
          {currentScreen === "warrants" && <WarrantCenterModule currentUser={currentUser} />}
          {currentScreen === "arrest-warrant-generator" && <WarrantCenterModule currentUser={currentUser} />}
          {currentScreen === "search-warrant-generator" && <WarrantCenterModule currentUser={currentUser} />}
          {currentScreen === "admin" && isAdmin(currentUser) && <AdminModule onBack={()=>handleNavigate("home")} onOpenReports={()=>handleNavigate("admin-reports")} onOpenDashboard={()=>handleNavigate("admin-dashboard")} onOpenUsers={()=>handleNavigate("users")} />}
          {currentScreen === "admin-reports" && isAdmin(currentUser) && <section>{renderPageTitle("Admin Reports", "Fuel, mileage, and export reporting.")}<AdminReportsModule currentUser={currentUser} /></section>}
          {currentScreen === "admin-dashboard" && isAdmin(currentUser) && <section>{renderPageTitle("Admin Dashboard", "Usage, login activity, audit activity, and pending approvals.")}<AdminDashboardModule refreshSignal={alertRefreshKey} onOpenUserManagement={() => handleNavigate("users")} /></section>}
          {currentScreen === "users" && isAdmin(currentUser) && <section>{renderPageTitle("User Management", "Approve users, adjust roles, and manage access.")}<UserManagementModule currentUser={currentUser} refreshSignal={alertRefreshKey} /></section>}
          {currentScreen === "change-password" && <section>{renderPageTitle("Change Password", "Update your account password.")}<ChangePasswordModule /></section>}
        </main>
      </div>

      <footer style={styles.footer}>
        <div style={styles.footerInnerWide}>
          <img src="/footer-logo.png" alt="Footer Logo" style={{ height:"35px", objectFit:"contain" }} />
          <div style={styles.footerText}>Created By M. Spangler — Aegis Group LLC</div>
        </div>
      </footer>
    </div>
  );
}

const styles = {
  headerRightTools:{display:"flex",alignItems:"center",gap:"10px"},
  headerNotepadWrap:{width:"280px",background:"rgba(15,23,42,0.72)",border:"1px solid rgba(148,163,184,0.35)",borderRadius:"12px",overflow:"hidden",boxShadow:"0 8px 18px rgba(0,0,0,0.18)"},
  headerNotepadTop:{display:"flex",alignItems:"center",justifyContent:"space-between",gap:"8px",padding:"7px 9px"},
  headerNotepadTitle:{fontWeight:800,fontSize:"12px",letterSpacing:"0.06em",textTransform:"uppercase",color:"#e5e7eb"},
  headerNotepadButton:{border:"1px solid rgba(255,255,255,0.25)",background:"rgba(255,255,255,0.10)",color:"#ffffff",borderRadius:"7px",padding:"3px 7px",fontSize:"11px",cursor:"pointer",width:"auto",minWidth:"auto"},
  headerNotepadTextarea:{width:"100%",height:"118px",border:"none",borderTop:"1px solid rgba(148,163,184,0.28)",outline:"none",resize:"vertical",padding:"9px",fontSize:"13px",lineHeight:1.35,boxSizing:"border-box",fontFamily:"Arial, sans-serif",background:"rgba(255,255,255,0.96)",color:"#111827"},
  notepadWrap:{position:"fixed",width:"260px",zIndex:50,background:"#ffffff",border:"1px solid #d1d5db",borderRadius:"12px",boxShadow:"0 10px 25px rgba(0,0,0,0.18)",overflow:"hidden"},
  notepadHeader:{display:"flex",justifyContent:"space-between",alignItems:"center",gap:"8px",background:"#111827",color:"#ffffff",padding:"8px 10px"},
  notepadTitle:{fontWeight:700,fontSize:"13px",letterSpacing:"0.02em"},
  notepadMiniButton:{border:"1px solid rgba(255,255,255,0.35)",background:"rgba(255,255,255,0.12)",color:"#ffffff",borderRadius:"7px",padding:"3px 7px",fontSize:"11px",cursor:"pointer",width:"auto",minWidth:"auto"},
  notepadTextarea:{width:"100%",height:"150px",border:"none",outline:"none",resize:"vertical",padding:"10px",fontSize:"13px",lineHeight:1.35,boxSizing:"border-box",fontFamily:"Arial, sans-serif"},
  app:{minHeight:"100vh",background:"#f3f4f6",color:"#111827",fontFamily:"Arial, sans-serif"},
  shell:{maxWidth:"1480px",margin:"0 auto",display:"grid",gridTemplateColumns:"280px minmax(0, 1fr)",gap:"18px",padding:"18px"},
  sidebar:{background:"#0f172a",border:"1px solid rgba(255,255,255,0.08)",borderRadius:"18px",padding:"18px",color:"#e5e7eb",boxShadow:"0 16px 40px rgba(15,23,42,0.22)",alignSelf:"start",position:"sticky",top:"18px"},
  sidebarBrand:{fontSize:"1.2rem",fontWeight:900,letterSpacing:"0.14em",color:"#ffffff",marginBottom:"10px"},
  sidebarUser:{fontSize:"0.9rem",lineHeight:1.35,color:"#cbd5e1",padding:"10px 12px",background:"rgba(255,255,255,0.06)",borderRadius:"12px",marginBottom:"18px"},
  sidebarGroup:{marginTop:"18px"},
  sidebarGroupTitle:{fontSize:"0.72rem",letterSpacing:"0.16em",fontWeight:800,color:"#93a4bd",margin:"0 0 8px 6px"},
  sidebarButton:{width:"100%",display:"flex",alignItems:"center",gap:"10px",border:"1px solid rgba(148,163,184,0.18)",background:"rgba(15,23,42,0.62)",color:"#e5e7eb",borderRadius:"12px",padding:"11px 12px",fontSize:"0.95rem",fontWeight:700,cursor:"pointer",marginBottom:"8px",textAlign:"left"},
  sidebarButtonActive:{background:"linear-gradient(135deg, rgba(30,64,175,0.86), rgba(15,23,42,0.9))",border:"1px solid rgba(96,165,250,0.65)",boxShadow:"0 0 0 1px rgba(96,165,250,0.15) inset"},
  sidebarIcon:{width:"22px",display:"inline-flex",justifyContent:"center",color:"#93c5fd"},
  sidebarLabel:{flex:1},
  sidebarBadge:{display:"inline-flex",alignItems:"center",justifyContent:"center",minWidth:"24px",height:"22px",padding:"0 7px",borderRadius:"999px",background:"#dc2626",color:"#ffffff",fontSize:"0.72rem",fontWeight:900},
  contentArea:{minWidth:0},
  headerInnerWide:{maxWidth:"1480px",margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",gap:"16px",flexWrap:"wrap"},
  footerInnerWide:{maxWidth:"1480px",margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",gap:"12px",flexWrap:"wrap"},
  pageTitleBlock:{marginBottom:"16px"},
  pageTitle:{fontSize:"1.8rem",lineHeight:1.1,margin:"0 0 4px",color:"#0f172a"},
  pageSubtitle:{margin:0,color:"#64748b",fontSize:"0.98rem"},
  commandGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(190px, 1fr))",gap:"14px",marginBottom:"18px"},
  commandCard:{background:"#ffffff",border:"1px solid #dbe4ef",borderRadius:"16px",padding:"18px",textAlign:"left",cursor:"pointer",boxShadow:"0 8px 24px rgba(15,23,42,0.08)"},
  commandCardLabel:{fontSize:"0.78rem",letterSpacing:"0.08em",fontWeight:900,color:"#475569",textTransform:"uppercase"},
  commandCardValue:{fontSize:"2.25rem",fontWeight:900,color:"#16213e",lineHeight:1,marginTop:"10px"},
  commandCardNote:{fontSize:"0.85rem",color:"#64748b",marginTop:"6px"},
  dashboardTwoColumn:{display:"grid",gridTemplateColumns:"minmax(0, 1.25fr) minmax(320px, 0.75fr)",gap:"18px",alignItems:"start"},
  quickEntryGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))",gap:"14px"},
  actionList:{display:"grid",gap:"10px"},
  actionRow:{width:"100%",border:"1px solid #dbe4ef",background:"#f8fafc",borderRadius:"12px",padding:"13px 14px",textAlign:"left",cursor:"pointer",display:"grid",gap:"3px"},
  segmentedControl:{display:"flex",gap:"6px",background:"#f1f5f9",border:"1px solid #dbe4ef",borderRadius:"12px",padding:"4px"},
  segmentButton:{border:"none",borderRadius:"9px",padding:"9px 12px",fontWeight:800,cursor:"pointer",background:"transparent",color:"#475569"},
  segmentButtonActive:{background:"#16213e",color:"#ffffff"},
  header:{background:"#16213e",color:"#ffffff",padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.15)"},
  headerInner:{maxWidth:"1200px",margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",gap:"16px",flexWrap:"wrap"},
  headerTitle:{fontSize:"1.4rem",fontWeight:"bold"},
  headerUser:{fontSize:"0.9rem",opacity:0.9,marginTop:"4px"},
  main:{maxWidth:"1200px",margin:"0 auto",padding:"18px"},
  sectionCard:{background:"#ffffff",borderRadius:"12px",padding:"18px",boxShadow:"0 2px 10px rgba(0,0,0,0.08)",marginBottom:"18px"},
  h2:{marginTop:0,marginBottom:"8px"},
  h3:{marginTop:0,marginBottom:"10px"},
  muted:{color:"#6b7280"},
  moduleGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))",gap:"16px",marginTop:"16px"},
  moduleCard:{background:"#ffffff",borderRadius:"12px",padding:"18px",boxShadow:"0 2px 10px rgba(0,0,0,0.08)",border:"1px solid #e5e7eb"},
  moduleCardHeader:{display:"flex",justifyContent:"space-between",alignItems:"center",gap:"10px",marginBottom:"10px",flexWrap:"wrap"},
  alertBadge:{display:"inline-flex",alignItems:"center",justifyContent:"center",minWidth:"28px",height:"28px",padding:"0 8px",borderRadius:"999px",background:"#dc2626",color:"#ffffff",fontSize:"0.8rem",fontWeight:700},
  alertTextBadge:{display:"inline-flex",alignItems:"center",justifyContent:"center",padding:"6px 12px",borderRadius:"999px",background:"#dc2626",color:"#ffffff",fontSize:"0.8rem",fontWeight:700,whiteSpace:"nowrap"},
  topActions:{display:"flex",gap:"10px",flexWrap:"wrap",marginBottom:"16px"},
  primaryButton:{border:"none",borderRadius:"10px",padding:"10px 14px",fontSize:"0.95rem",cursor:"pointer",background:"#16213e",color:"#ffffff"},
  secondaryButton:{border:"none",borderRadius:"10px",padding:"10px 14px",fontSize:"0.95rem",cursor:"pointer",background:"#6b7280",color:"#ffffff"},
  successButton:{border:"none",borderRadius:"10px",padding:"10px 14px",fontSize:"0.95rem",cursor:"pointer",background:"#065f46",color:"#ffffff"},
  dangerButton:{border:"none",borderRadius:"10px",padding:"10px 14px",fontSize:"0.95rem",cursor:"pointer",background:"#b91c1c",color:"#ffffff"},
  permissionPill:{display:"inline-block",padding:"6px 10px",borderRadius:"999px",background:"#e5e7eb",color:"#374151",fontSize:"0.8rem",fontWeight:600},
  activePill:{display:"inline-block",padding:"6px 10px",borderRadius:"999px",background:"#dcfce7",color:"#166534",fontSize:"0.8rem",fontWeight:700},
  disabledPill:{display:"inline-block",padding:"6px 10px",borderRadius:"999px",background:"#fee2e2",color:"#991b1b",fontSize:"0.8rem",fontWeight:700},
  highPriorityPill:{display:"inline-block",padding:"6px 10px",borderRadius:"999px",background:"#fee2e2",color:"#991b1b",fontSize:"0.8rem",fontWeight:700},
  normalPriorityPill:{display:"inline-block",padding:"6px 10px",borderRadius:"999px",background:"#dbeafe",color:"#1d4ed8",fontSize:"0.8rem",fontWeight:700},
  lowPriorityPill:{display:"inline-block",padding:"6px 10px",borderRadius:"999px",background:"#dcfce7",color:"#166534",fontSize:"0.8rem",fontWeight:700},
  form:{display:"grid",gap:"14px"},
  formGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))",gap:"20px",rowGap:"20px"},
  label:{display:"block",fontWeight:"bold",marginBottom:"6px"},
  input:{width:"100%",boxSizing:"border-box",padding:"10px",borderRadius:"10px",border:"1px solid #cbd5e1",fontSize:"0.95rem"},
  textarea:{width:"100%",boxSizing:"border-box",minHeight:"120px",padding:"10px",borderRadius:"10px",border:"1px solid #cbd5e1",fontSize:"0.95rem",fontFamily:"Arial, sans-serif",resize:"vertical"},
  compactInput:{width:"100%",boxSizing:"border-box",minWidth:"120px",padding:"8px",borderRadius:"8px",border:"1px solid #cbd5e1",fontSize:"0.9rem"},
  vacationList:{display:"grid",gap:"12px"},
  vacationCard:{background:"#ffffff",border:"1px solid #dbe1e8",borderRadius:"12px",padding:"14px",boxShadow:"0 1px 6px rgba(0,0,0,0.05)"},
  vacationCardHeader:{display:"flex",justifyContent:"space-between",alignItems:"center",gap:"10px",flexWrap:"wrap",marginBottom:"10px"},
  codeBadge:{display:"inline-block",background:"#16213e",color:"#ffffff",padding:"6px 10px",borderRadius:"999px",fontWeight:"bold",letterSpacing:"0.5px"},
  detailGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))",gap:"8px 14px",marginTop:"8px"},
  detailItem:{fontSize:"0.95rem"},
  detailLabel:{fontWeight:"bold",color:"#374151"},
  smallText:{fontSize:"0.9rem",color:"#6b7280"},
  passOnCollapseButton:{width:"100%",background:"transparent",border:"none",padding:0,margin:0,cursor:"pointer",textAlign:"left"},
  passOnToggleText:{fontSize:"0.85rem",fontWeight:700,color:"#4b5563"},
  passOnTitleRow:{marginTop:8,fontWeight:700,textAlign:"left",color:"#111827"},
  authWrapper:{minHeight:"100vh",background:"#f3f4f6",display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",fontFamily:"Arial, sans-serif"},
  authCard:{width:"100%",maxWidth:"460px",background:"#ffffff",borderRadius:"16px",padding:"24px",boxShadow:"0 10px 30px rgba(0,0,0,0.10)"},
  authTitle:{marginTop:0,marginBottom:"8px",fontSize:"1.7rem",color:"#16213e"},
  authSubtitle:{marginTop:0,marginBottom:"18px",color:"#6b7280"},
  authToggleRow:{display:"flex",gap:"10px",marginBottom:"18px"},
  authToggleButton:{flex:1,padding:"10px 14px",borderRadius:"10px",border:"1px solid #cbd5e1",background:"#fff",cursor:"pointer",fontWeight:600},
  authToggleActive:{background:"#16213e",color:"#fff",border:"1px solid #16213e"},
  userTableWrapper:{overflowX:"auto"},
  userTable:{width:"100%",borderCollapse:"collapse"},
  userTableTh:{textAlign:"left",padding:"10px",borderBottom:"1px solid #e5e7eb",fontSize:"0.9rem"},
  userTableTd:{padding:"10px",borderBottom:"1px solid #f1f5f9",verticalAlign:"middle"},
  trafficWrapper:{maxWidth:"980px",margin:"0 auto",padding:0,fontFamily:"system-ui, sans-serif"},
  jurisRow:{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"},
  filterButton:{padding:"8px 12px",borderRadius:10,border:"1px solid #ccc",cursor:"pointer"},
  inlineRow:{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"},
  smallInput:{width:110,padding:10,borderRadius:10,border:"1px solid #ccc"},
  tinyText:{fontSize:12,opacity:0.7},
  searchInput:{width:"100%",padding:12,borderRadius:10,border:"1px solid #ccc",marginBottom:12},
  trafficGrid:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12},
  resultsPanel:{border:"1px solid #ddd",borderRadius:12,padding:12,minHeight:420},
  detailsPanel:{border:"1px solid #ddd",borderRadius:12,padding:12},
  panelTitle:{fontWeight:700,marginBottom:8},
  emptyState:{opacity:0.75},
  resultRow:{padding:8,borderBottom:"1px solid #eee",cursor:"pointer"},
  resultCitation:{fontWeight:600},
  resultDescription:{fontSize:13},
  detailLine:{marginBottom:6},
  moneyGrid:{marginTop:10,display:"grid",gridTemplateColumns:"1fr 1fr",gap:8},
  footer:{background:"#16213e",color:"#ffffff",padding:"14px 20px",marginTop:"20px"},
  footerInner:{maxWidth:"1200px",margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",gap:"12px",flexWrap:"wrap"},
  footerText:{fontSize:"0.9rem",opacity:0.9},
  summaryGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))",gap:"12px",marginBottom:"18px"},
  documentMetaRow:{display:"flex",gap:"8px",flexWrap:"wrap"},
  summaryCard:{background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:"12px",padding:"16px"},
  summaryLabel:{fontSize:"0.85rem",color:"#64748b",marginBottom:"6px"},
  summaryValue:{fontSize:"1.6rem",fontWeight:700,color:"#0f172a"},
};
